<template>
  <div class="entity-card" :class="{ expanded: isExpanded, highlighted: isHighlighted }">
    <!-- Collapsed View — gap-led, dense -->
    <div class="entity-summary" @click="toggleExpand">
      <div class="entity-header">
        <div class="entity-info">
          <div class="entity-title-row">
            <h3>{{ entity.subject_entity_name }}</h3>
            <span class="status-badge" :class="statusClass">
              {{ statusLabel }}
            </span>
          </div>
          <p v-if="issueHeadline" class="issue-headline" :class="{ 'issue-ok': isCompliantStatus }">
            {{ issueHeadline }}
          </p>
          <p v-if="recommendedAction" class="issue-action">
            <span class="issue-action-label">Next:</span> {{ recommendedAction }}
          </p>
          <p v-else-if="nextExpiryLine" class="issue-action issue-action--watch">
            <span class="issue-action-label">Watch:</span> {{ nextExpiryLine }}
          </p>
        </div>
        <div class="header-actions">
          <button
            class="record-link-btn"
            title="View full compliance record"
            @click.stop="openEntityRecord()"
          >
            <i class="fas fa-book-open"></i> Record
          </button>
          <button class="expand-btn" @click.stop="toggleExpand" :aria-expanded="isExpanded">
            <i :class="isExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
          </button>
        </div>
      </div>

      <div class="entity-stats">
        <span v-if="nextExpiryLine && recommendedAction" class="stat-expiry">
          <i class="fas fa-calendar-alt"></i> {{ nextExpiryLine }}
        </span>
        <span
          v-if="outreachLine"
          class="stat-outreach"
          :class="{ 'stat-outreach--problem': outreachProblem }"
          :title="entity.issue_summary.outreach.to_email"
        >
          <i class="fas fa-envelope"></i> {{ outreachLine }}
        </span>
        <span v-if="entity.agent_links && entity.agent_links.length > 0">
          <i class="fas fa-user-tie"></i>
          {{ entity.agent_links.length }}
          {{ entity.agent_links.length === 1 ? 'agent' : 'agents' }}
        </span>
        <button
          v-if="continuityChipLabel"
          type="button"
          class="continuity-chip"
          :class="continuityChipClass"
          :title="continuityChipTitle"
          @click.stop="openEntityRecord(continuityDefaultTab)"
        >
          <i class="fas fa-shield-alt"></i> {{ continuityChipLabel }}
        </button>
        <span v-if="locationCountHint" class="stat-locations" :title="locationCountTitle">
          <i class="fas fa-map-marker-alt"></i> {{ locationCountHint }}
        </span>
      </div>
    </div>
    
    <!-- Expanded View - Split Layout -->
    <div v-if="isExpanded" class="entity-details split-view">
      <!-- Left Panel: Overview (Always Visible) -->
      <div class="overview-panel">
        <!-- Compliance Summary -->
        <div class="summary-section">
          <h4><i class="fas fa-clipboard-check"></i> Compliance</h4>
          <div v-if="loadingStatus" class="loading-mini">
            <i class="fas fa-spinner fa-spin"></i>
          </div>
          <div v-else-if="complianceStatus">
            <div class="status-display">
              <span class="status-badge" :class="statusClass">
                {{ entity.current_status }}
              </span>
            </div>
            <p class="requirements-summary">
              {{ complianceStatus.passed || 0 }}/{{ complianceStatus.total || 0 }} requirements met
            </p>
            <p class="last-checked">
              Last checked: {{ formatDate(entity.updated_at) }}
            </p>
            <!-- TODO: Add proper requirements view in gap analysis/decision center -->
            <!-- <button @click="viewAllRequirements" class="btn-link">
              View All Requirements →
            </button> -->
          </div>
        </div>
        
        <!-- Coverage History Summary -->
        <div v-if="entity.continuity_summary" class="summary-section">
          <h4><i class="fas fa-shield-alt"></i> Coverage History</h4>
          <div v-if="!entity.continuity_summary.has_interval_data" class="continuity-mini continuity-mini-empty">
            <p class="doc-count">No coverage intervals tracked yet</p>
            <button @click="openEntityRecord('continuity')" class="btn-link">
              View coverage history →
            </button>
          </div>
          <div v-else class="continuity-mini">
            <div class="continuity-mini-bar-track">
              <div
                class="continuity-mini-bar-fill"
                :class="continuityMiniBarClass"
                :style="{ width: `${Math.min(entity.continuity_summary.pct_covered || 0, 100)}%` }"
              ></div>
            </div>
            <p class="continuity-mini-meta">
              {{ Math.round(entity.continuity_summary.pct_covered || 0) }}% covered
              <span v-if="entity.continuity_summary.lapse_count">
                · {{ entity.continuity_summary.lapse_count }}
                {{ entity.continuity_summary.lapse_count === 1 ? 'lapse' : 'lapses' }}
              </span>
            </p>
            <button @click="openEntityRecord('continuity')" class="btn-link">
              View coverage history →
            </button>
          </div>
        </div>
        
        <!-- Current Documents Summary -->
        <div class="summary-section">
          <h4><i class="fas fa-file-alt"></i> Current Documents</h4>
          <div v-if="loadingDocuments" class="loading-mini">
            <i class="fas fa-spinner fa-spin"></i>
          </div>
          <div v-else>
            <p class="doc-count">{{ currentDocuments?.length || 0 }} active</p>
            <p v-if="expiringCount > 0" class="expiring-warning">
              ⚠️ {{ expiringCount }} expiring soon
            </p>
            <button @click="openEntityRecord('documents')" class="btn-link">
              View Documents →
            </button>
          </div>
        </div>
        
        <!-- History Summary -->
        <div class="summary-section">
          <h4><i class="fas fa-history"></i> History</h4>
          <p class="doc-count">{{ historicalDocuments?.length || 0 }} older documents</p>
          <button @click="openEntityRecord('timeline')" class="btn-link">
            View History →
          </button>
        </div>
        
        <!-- Last Updated -->
        <div class="summary-section">
          <h4><i class="fas fa-clock"></i> Last Updated</h4>
          <p>{{ formatDate(entity.updated_at) }}</p>
        </div>
        
        <!-- Actions -->
        <div class="panel-actions">
          <button @click="openEntityRecord()" class="btn btn-primary btn-sm full-record-btn">
            <i class="fas fa-book-open"></i> View Full Record
          </button>
          <button @click="collapse" class="btn btn-outline btn-sm">
            <i class="fas fa-chevron-up"></i> Collapse
          </button>
          <button @click="openEditEntity" class="btn btn-outline btn-sm" :title="`Edit ${entityTerm.singular} details`">
            <i class="fas fa-edit"></i> Edit
          </button>
          <button @click="addStore" class="btn btn-success btn-sm" title="Add a new store/location to this entity">
            <i class="fas fa-plus-circle"></i> Add Store
          </button>
          <button @click="uploadDocument" class="btn btn-primary btn-sm">
            <i class="fas fa-upload"></i> Upload
          </button>
          <button @click="requestDocuments" class="btn btn-outline btn-sm">
            <i class="fas fa-envelope"></i> Request
          </button>
          <button @click="confirmArchive" class="btn btn-outline btn-sm btn-danger-outline" title="Archive this entity">
            <i class="fas fa-archive"></i> Archive
          </button>
        </div>
      </div>
      
      <!-- Right Panel: Details (Scrollable) -->
      <div class="details-panel">
        <!-- Locations Section -->
        <div class="detail-section locations-section" id="locations">
          <div class="section-header" @click="toggleSection('locations')">
            <h4>
              <i class="fas fa-map-marker-alt"></i>
              {{ locationTerm.plural }} ({{ locations?.length || 0 }})
            </h4>
            <i :class="['toggle-icon', 'fas', expanded.locations ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
          </div>
          <div v-if="expanded.locations" class="section-content">
            <div v-if="loadingLocations" class="loading-mini">
              <i class="fas fa-spinner fa-spin"></i> Loading {{ locationTerm.plural.toLowerCase() }}...
            </div>
            <div v-else-if="locations && locations.length > 0">
              <p class="locations-coverage-hint">
                One certificate may cover some, all, or none of these
                {{ locationTerm.plural.toLowerCase() }}. Edge color shows insured status when known.
              </p>
              <div class="locations-badges-container">
                <div
                  v-for="location in locations"
                  :key="location.id"
                  class="location-badge"
                  :class="locationCoverageClass(location)"
                  :title="locationCoverageTitle(location)"
                >
                  <i class="fas fa-map-marker-alt"></i>
                  <span v-if="location.external_location_id">#{{ location.external_location_id }}</span>
                  <span v-if="location.external_location_id && location.name"> — </span>
                  <span v-if="location.name">{{ location.name }}</span>
                  <span v-if="!location.external_location_id && !location.name">Location</span>
                  <span class="location-cov-label">{{ locationCoverageLabel(location) }}</span>
                </div>
              </div>
            </div>
            <div v-else class="empty-mini">
              No {{ locationTerm.plural.toLowerCase() }} yet
            </div>
          </div>
        </div>
        
        <!-- Agents & Contacts Section -->
        <div class="detail-section agents-section" id="agents">
          <div class="section-header" @click="toggleSection('agents')">
            <h4>
              <i class="fas fa-user-tie"></i>
              Contacts & Insurance ({{ subjectAgents.length + (entityContacts?.length || 0) }})
            </h4>
            <i :class="['toggle-icon', 'fas', expanded.agents ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
          </div>
          <div v-if="expanded.agents" class="section-content">

            <!-- ── Store Staff (owner, GM, operations — non-insurance roles) ── -->
            <div class="contacts-subsection">
              <div class="subsection-header">
                <span class="subsection-title">
                  <i class="fas fa-user"></i> {{ entityTerm.singular }} Staff
                  <span class="count-chip">{{ storeContacts.length }}</span>
                </span>
                <button
                  v-if="!showAddContactForm"
                  @click="showAddContactForm = true; addContactError = null"
                  class="btn btn-success btn-xs"
                >
                  <i class="fas fa-plus"></i> Add Staff Contact
                </button>
              </div>

              <!-- Loading -->
              <div v-if="loadingContacts" class="loading-mini">
                <i class="fas fa-spinner fa-spin"></i> Loading contacts...
              </div>

              <!-- Staff contact list (non-insurance roles only) -->
              <div v-else-if="storeContacts.length > 0" class="direct-contacts-list">
                <div
                  v-for="contact in storeContacts"
                  :key="contact.id"
                  class="direct-contact-card"
                >
                  <div class="direct-contact-info">
                    <div class="direct-contact-name">
                      <strong>{{ contact.name }}</strong>
                      <span v-if="contact.contact_role_display" class="contact-role-badge">
                        {{ contact.contact_role_display }}
                      </span>
                      <span v-if="contact.is_primary" class="primary-badge">Primary</span>
                    </div>
                    <div class="direct-contact-details">
                      <span v-if="contact.email"><i class="fas fa-envelope"></i> {{ contact.email }}</span>
                      <span v-if="contact.phone"><i class="fas fa-phone"></i> {{ contact.phone }}</span>
                      <span v-if="contact.title" class="contact-title-text">{{ contact.title }}</span>
                    </div>
                  </div>
                  <button
                    @click="deleteContact(contact.id)"
                    class="btn-remove-contact"
                    title="Remove contact"
                  ><i class="fas fa-times"></i></button>
                </div>
              </div>
              <div v-else-if="!loadingContacts && entityContacts !== null" class="empty-mini">
                No staff contacts yet
              </div>

              <!-- Add Contact inline form -->
              <div v-if="showAddContactForm" class="add-contact-inline">
                <div v-if="addContactError" class="contact-form-error">{{ addContactError }}</div>
                <div class="contact-form-row">
                  <div class="contact-form-field">
                    <label>Name <span class="req">*</span></label>
                    <input v-model="newContact.name" type="text" class="form-ctrl" placeholder="Full name" />
                  </div>
                  <div class="contact-form-field">
                    <label>Role <span class="req">*</span></label>
                    <select v-model="newContact.contact_role" class="form-ctrl">
                      <option value="">-- Select --</option>
                      <option v-for="r in CONTACT_ROLES.filter(r => r.group !== 'Insurance Agency')" :key="r.value" :value="r.value">{{ r.label }}</option>
                    </select>
                  </div>
                </div>
                <div class="contact-form-row">
                  <div class="contact-form-field">
                    <label>Email <span class="req">*</span></label>
                    <input
                      v-model="newContact.email"
                      type="email"
                      class="form-ctrl"
                      placeholder="email@example.com"
                      @blur="checkContactEmail(newContact.email, entity.subject_entity_id || entity.subject_entity, 'newContactEmailWarning')"
                    />
                    <div v-if="newContactEmailWarning" :class="['email-warning', newContactEmailWarning.type === 'duplicate' ? 'email-warning-error' : 'email-warning-caution']">
                      <i :class="newContactEmailWarning.type === 'duplicate' ? 'fas fa-times-circle' : 'fas fa-exclamation-triangle'"></i>
                      {{ newContactEmailWarning.message }}
                    </div>
                  </div>
                  <div class="contact-form-field">
                    <label>Phone</label>
                    <input v-model="newContact.phone" type="tel" class="form-ctrl" placeholder="(555) 123-4567" />
                  </div>
                </div>
                <div class="contact-form-field">
                  <label>Job Title <small>(optional)</small></label>
                  <input v-model="newContact.title" type="text" class="form-ctrl" placeholder="e.g., General Manager" />
                </div>
                <div class="contact-form-actions">
                  <button
                    @click="submitAddContact"
                    class="btn btn-success btn-sm"
                    :disabled="savingContact || !newContact.name || !newContact.email || !newContact.contact_role"
                  >
                    <i v-if="savingContact" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-check"></i>
                    {{ savingContact ? 'Saving...' : 'Save Contact' }}
                  </button>
                  <button
                    @click="showAddContactForm = false; addContactError = null"
                    class="btn btn-secondary btn-sm"
                  >Cancel</button>
                </div>
              </div>
            </div>

            <!-- ── Insurance Agents & Contacts ── -->
            <div class="agents-subsection">
              <div class="subsection-header">
                <span class="subsection-title">
                  <i class="fas fa-shield-alt"></i> Insurance Agents &amp; Contacts
                  <span class="count-chip">{{ subjectAgents.length + insurancePersonContacts.length }}</span>
                </span>
                <div class="subsection-actions" v-if="!showAddInsurancePersonForm && !showAddAgentForm">
                  <button
                    @click="showAddInsurancePersonForm = true; addInsurancePersonError = null; resetInsurancePerson()"
                    class="btn btn-success btn-xs"
                  >
                    <i class="fas fa-user-plus"></i> Add Agent / Contact
                  </button>
                  <button
                    @click="showAddAgentForm = true; addAgentError = null; editingAgentLinkId = null"
                    class="btn btn-outline btn-xs"
                    title="Add by agency name — use when you have the full agency information"
                  >
                    <i class="fas fa-building"></i> Add by Agency
                  </button>
                </div>
              </div>

              <!-- Person-first form: name/email first, agency optional -->
              <div v-if="showAddInsurancePersonForm" class="add-insurance-person-form">
                <div class="insurance-person-form-header">
                  <i class="fas fa-user-tie"></i>
                  <strong>Add Insurance Agent / Contact</strong>
                  <p class="form-hint">Enter the agent's name and email. You can add their agency later when the certificate arrives.</p>
                </div>

                <div v-if="addInsurancePersonError" class="contact-form-error">{{ addInsurancePersonError }}</div>

                <div class="contact-form-row">
                  <div class="contact-form-field">
                    <label>Name <span class="req">*</span></label>
                    <input v-model="newInsurancePerson.name" type="text" class="form-ctrl" placeholder="e.g. John Smith" autofocus />
                  </div>
                  <div class="contact-form-field">
                    <label>Role</label>
                    <select v-model="newInsurancePerson.contact_role" class="form-ctrl">
                      <option value="INSURANCE_AGENT">Insurance Agent / Producer</option>
                      <option value="ACCOUNT_MANAGER">Account Manager</option>
                      <option value="CLAIMS_HANDLER">Claims Handler</option>
                    </select>
                  </div>
                </div>
                <div class="contact-form-row">
                  <div class="contact-form-field">
                    <label>Email <span class="req">*</span></label>
                    <input
                      v-model="newInsurancePerson.email"
                      type="email"
                      class="form-ctrl"
                      placeholder="john@statefarm.com"
                      @blur="checkContactEmail(newInsurancePerson.email, newInsurancePerson.selectedAgency?.id || (entity.subject_entity_id || entity.subject_entity), 'insurancePersonEmailWarning')"
                    />
                    <div v-if="insurancePersonEmailWarning" :class="['email-warning', insurancePersonEmailWarning.type === 'duplicate' ? 'email-warning-error' : 'email-warning-caution']">
                      <i :class="insurancePersonEmailWarning.type === 'duplicate' ? 'fas fa-times-circle' : 'fas fa-exclamation-triangle'"></i>
                      {{ insurancePersonEmailWarning.message }}
                    </div>
                  </div>
                  <div class="contact-form-field">
                    <label>Phone</label>
                    <input v-model="newInsurancePerson.phone" type="tel" class="form-ctrl" placeholder="(555) 123-4567" />
                  </div>
                </div>

                <!-- Optional agency -->
                <div class="agency-optional-section">
                  <div class="agency-optional-label">
                    <i class="fas fa-building"></i>
                    <strong>Agency / Broker</strong>
                    <span class="optional-tag">optional</span>
                  </div>
                  <p class="agency-optional-hint">
                    <i class="fas fa-info-circle"></i>
                    Don't know the agency yet? That's fine — leave this blank. The agency will be linked automatically when we receive their certificate.
                  </p>

                  <!-- Agency search input -->
                  <div v-if="!newInsurancePerson.selectedAgency">
                    <input
                      v-model="newInsurancePerson.agencySearchQuery"
                      type="text"
                      class="form-ctrl"
                      placeholder="Search agency name (optional)..."
                      @input="debouncedInsuranceAgencySearch"
                      autocomplete="off"
                    />
                    <!-- Search results -->
                    <div v-if="newInsurancePerson.agencySearchResults.length > 0" class="agent-search-results">
                      <div
                        v-for="e in newInsurancePerson.agencySearchResults"
                        :key="e.id"
                        class="agent-search-item"
                        @click="newInsurancePerson.selectedAgency = e; newInsurancePerson.agencySearchQuery = e.name; newInsurancePerson.agencySearchResults = []"
                      >
                        <strong>{{ e.name }}</strong>
                        <span class="text-muted small ml-1">{{ e.entity_type }}</span>
                      </div>
                    </div>
                    <!-- Create new agency option -->
                    <div
                      v-if="newInsurancePerson.agencySearchQuery.length >= 3 && !newInsurancePerson.isSearchingAgency && newInsurancePerson.agencySearchResults.length === 0"
                      class="create-agency-hint mt-1"
                    >
                      <button type="button" @click="createInsurancePersonAgency" class="btn-text-sm">
                        <i class="fas fa-plus"></i> Create agency: <strong>"{{ newInsurancePerson.agencySearchQuery }}"</strong>
                      </button>
                    </div>
                  </div>

                  <!-- Selected agency chip -->
                  <div v-else class="selected-agent-chip">
                    <i class="fas fa-check-circle text-success"></i>
                    <strong>{{ newInsurancePerson.selectedAgency.name }}</strong>
                    <button @click="newInsurancePerson.selectedAgency = null; newInsurancePerson.agencySearchQuery = ''" class="chip-clear ml-2">×</button>
                  </div>
                </div>

                <div class="contact-form-actions">
                  <button
                    @click="submitAddInsurancePerson"
                    class="btn btn-success btn-sm"
                    :disabled="savingAgent || !newInsurancePerson.name || !newInsurancePerson.email"
                  >
                    <i v-if="savingAgent" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-check"></i>
                    {{ savingAgent ? 'Saving...' : 'Save' }}
                  </button>
                  <button @click="showAddInsurancePersonForm = false; resetInsurancePerson()" class="btn btn-secondary btn-sm">Cancel</button>
                </div>
              </div>

              <!-- Standalone insurance contacts (no agency yet) -->
              <div v-if="insurancePersonContacts.length > 0" class="standalone-insurance-contacts">
                <div
                  v-for="contact in insurancePersonContacts"
                  :key="contact.id"
                  class="standalone-contact-card"
                >
                  <div class="standalone-contact-info">
                    <div class="standalone-contact-name">
                      <strong>{{ contact.name }}</strong>
                      <span v-if="contact.contact_role_display" class="contact-role-badge">{{ contact.contact_role_display }}</span>
                    </div>
                    <div class="standalone-contact-details">
                      <span v-if="contact.email"><i class="fas fa-envelope"></i> {{ contact.email }}</span>
                      <span v-if="contact.phone"><i class="fas fa-phone"></i> {{ contact.phone }}</span>
                    </div>
                    <div class="no-agency-badge">
                      <i class="fas fa-clock"></i> No agency linked yet — will be auto-matched when certificate arrives
                    </div>
                  </div>
                  <button
                    @click="deleteContact(contact.id)"
                    class="btn-remove-contact"
                    title="Remove"
                  ><i class="fas fa-times"></i></button>
                </div>
              </div>

              <!-- Loading -->
              <div v-if="loadingAgents" class="loading-mini">
                <i class="fas fa-spinner fa-spin"></i> Loading agents...
              </div>

              <!-- Agent list -->
              <div v-else-if="subjectAgents.length > 0">
                <div v-for="link in subjectAgents" :key="link.id" class="agent-card">

                  <!-- ── View mode ── -->
                  <template v-if="editingAgentLinkId !== link.id">
                    <div class="agent-header">
                      <strong>{{ link.agent_entity_name }}</strong>
                      <div class="agent-header-right">
                        <span class="role-badge">{{ link.agent_entity_type || 'Agency' }}</span>
                        <span v-if="link.linked_automatically" class="auto-badge" title="Linked automatically from COI">
                          <i class="fas fa-robot"></i> Auto
                        </span>
                      </div>
                    </div>

                    <!-- Coverage pills -->
                    <div class="coverage-pills">
                      <template v-if="link.coverage_types && link.coverage_types.length > 0">
                        <span v-for="cov in link.coverage_types" :key="cov" class="coverage-pill">{{ cov }}</span>
                      </template>
                      <span v-else class="coverage-pill coverage-pill-all">All Coverage</span>
                    </div>

                    <!-- Agency contact info -->
                    <div v-if="link.agent_entity_email || link.agent_entity_phone" class="agent-entity-contact">
                      <p v-if="link.agent_entity_email" class="contact-item">
                        <i class="fas fa-envelope"></i> {{ link.agent_entity_email }}
                      </p>
                      <p v-if="link.agent_entity_phone" class="contact-item">
                        <i class="fas fa-phone"></i> {{ link.agent_entity_phone }}
                      </p>
                    </div>

                    <!-- Contacts at this agency -->
                    <div v-if="agencyContacts[link.agent_entity_id] && agencyContacts[link.agent_entity_id].length > 0" class="agency-contacts-list">
                      <div v-for="c in agencyContacts[link.agent_entity_id]" :key="c.id" class="agency-contact-row">
                        <i class="fas fa-user-circle"></i>
                        <span class="agency-contact-name">{{ c.name }}</span>
                        <span v-if="c.contact_role_display" class="contact-role-badge sm">{{ c.contact_role_display }}</span>
                        <span v-if="c.email" class="agency-contact-detail">{{ c.email }}</span>
                        <span v-if="c.phone" class="agency-contact-detail">{{ c.phone }}</span>
                      </div>
                    </div>

                    <p v-if="link.notes" class="agent-notes"><i class="fas fa-sticky-note"></i> {{ link.notes }}</p>

                    <div class="agent-card-actions">
                      <button @click="startEditAgent(link)" class="btn btn-outline btn-sm">
                        <i class="fas fa-edit"></i> Edit
                      </button>
                      <button @click="requestFromAgentLink(link)" class="btn btn-outline btn-sm">
                        <i class="fas fa-paper-plane"></i> Request
                      </button>
                      <button @click="removeAgent(link.id)" class="btn btn-sm btn-danger-outline" title="Remove agent link">
                        <i class="fas fa-unlink"></i> Remove
                      </button>
                    </div>
                  </template>

                  <!-- ── Edit mode ── -->
                  <template v-else>
                    <div class="agent-edit-header">
                      <strong>Editing: {{ link.agent_entity_name }}</strong>
                    </div>
                    <div v-if="editAgentError" class="contact-form-error">{{ editAgentError }}</div>

                    <!-- Change agency -->
                    <div class="agent-form-step">
                      <div class="agent-form-step-label">
                        <span class="step-num">1</span> Agency / Brokerage
                        <button @click="editForm.changingAgency = !editForm.changingAgency" class="btn-text-link">
                          {{ editForm.changingAgency ? 'Keep current' : 'Change agency' }}
                        </button>
                      </div>
                      <div v-if="!editForm.changingAgency" class="selected-agent-chip">
                        <i class="fas fa-building"></i>
                        <strong>{{ link.agent_entity_name }}</strong>
                      </div>
                      <div v-else>
                        <div class="agent-search-wrap">
                          <input
                            v-model="editForm.agencySearchQuery"
                            @input="debouncedEditAgencySearch"
                            type="text"
                            class="form-ctrl"
                            placeholder="Search for a different agency..."
                            autocomplete="off"
                          />
                          <span v-if="editForm.searchingAgency" class="search-spinner"><i class="fas fa-spinner fa-spin"></i></span>
                        </div>
                        <div v-if="editForm.agencySearchResults.length > 0 && !editForm.selectedNewAgency" class="agent-search-results">
                          <div
                            v-for="e in editForm.agencySearchResults"
                            :key="e.id"
                            class="agent-search-result-item"
                            @click="editForm.selectedNewAgency = e; editForm.agencySearchQuery = e.name; editForm.agencySearchResults = []"
                          >
                            <strong>{{ e.name }}</strong>
                            <span class="result-type">{{ e.entity_type }}</span>
                          </div>
                        </div>
                        <div
                          v-if="editForm.agencySearchQuery.length >= 3 && !editForm.selectedNewAgency && !editForm.searchingAgency && editForm.agencySearchResults.length === 0"
                          class="agent-create-new"
                          @click="createAndSelectEditAgency()"
                        >
                          <i class="fas fa-plus-circle"></i>
                          Create new agency: <strong>"{{ editForm.agencySearchQuery }}"</strong>
                        </div>
                        <div v-if="editForm.selectedNewAgency" class="selected-agent-chip">
                          <i class="fas fa-building"></i>
                          <strong>{{ editForm.selectedNewAgency.name }}</strong>
                          <button @click="editForm.selectedNewAgency = null; editForm.agencySearchQuery = ''" class="chip-clear">
                            <i class="fas fa-times"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Edit coverage types -->
                    <div class="agent-form-step">
                      <div class="agent-form-step-label">
                        <span class="step-num">2</span> Coverage types
                        <small class="step-hint">(leave blank = handles all)</small>
                      </div>
                      <div class="coverage-checkboxes">
                        <label v-for="ct in AGENT_COVERAGE_TYPES" :key="ct.value" class="coverage-checkbox-item">
                          <input type="checkbox" :value="ct.value" v-model="editForm.coverageTypes" />
                          {{ ct.label }}
                        </label>
                      </div>
                    </div>

                    <!-- Edit / add contact at agency -->
                    <div class="agent-form-step">
                      <div class="agent-form-step-label">
                        <span class="step-num">3</span> Contacts at {{ link.agent_entity_name }}
                      </div>

                      <!-- Existing contacts -->
                      <div v-if="agencyContacts[link.agent_entity_id] && agencyContacts[link.agent_entity_id].length > 0" class="agency-edit-contacts">
                        <div v-for="c in agencyContacts[link.agent_entity_id]" :key="c.id" class="agency-edit-contact-row">
                          <span class="agency-contact-name">{{ c.name }}</span>
                          <span v-if="c.contact_role_display" class="contact-role-badge sm">{{ c.contact_role_display }}</span>
                          <span v-if="c.email" class="agency-contact-detail">{{ c.email }}</span>
                          <button @click="deleteAgencyContact(c.id, link.agent_entity_id)" class="btn-remove-contact" title="Remove">
                            <i class="fas fa-times"></i>
                          </button>
                        </div>
                      </div>

                      <!-- Add contact toggle -->
                      <div v-if="!editForm.showContactFields" class="agent-contact-toggle" style="margin-top: 6px;">
                        <button @click="editForm.showContactFields = true" class="btn-add-person">
                          <i class="fas fa-user-plus"></i> Add contact person
                        </button>
                      </div>
                      <div v-else class="agent-contact-fields">
                        <div class="contact-form-row">
                          <div class="contact-form-field">
                            <label>Name</label>
                            <input v-model="editForm.contact.name" type="text" class="form-ctrl" placeholder="Full name" />
                          </div>
                          <div class="contact-form-field">
                            <label>Role</label>
                            <select v-model="editForm.contact.contact_role" class="form-ctrl">
                              <option value="INSURANCE_AGENT">Insurance Agent / Producer</option>
                              <option value="ACCOUNT_MANAGER">Account Manager</option>
                              <option value="CLAIMS_HANDLER">Claims Handler</option>
                              <option value="OTHER">Other</option>
                            </select>
                          </div>
                        </div>
                        <div class="contact-form-row">
                          <div class="contact-form-field">
                            <label>Email</label>
                            <input
                              v-model="editForm.contact.email"
                              type="email"
                              class="form-ctrl"
                              placeholder="email@agency.com"
                              @blur="checkContactEmail(editForm.contact.email, editForm.selectedNewAgency && editForm.selectedNewAgency.id, 'editContactEmailWarning')"
                            />
                            <div v-if="editContactEmailWarning" :class="['email-warning', editContactEmailWarning.type === 'duplicate' ? 'email-warning-error' : 'email-warning-caution']">
                              <i :class="editContactEmailWarning.type === 'duplicate' ? 'fas fa-times-circle' : 'fas fa-exclamation-triangle'"></i>
                              {{ editContactEmailWarning.message }}
                            </div>
                          </div>
                          <div class="contact-form-field">
                            <label>Phone</label>
                            <input v-model="editForm.contact.phone" type="tel" class="form-ctrl" placeholder="(555) 123-4567" />
                          </div>
                        </div>
                        <button @click="editForm.showContactFields = false; editForm.contact = resetContactObj(); editContactEmailWarning = null" class="btn-text-sm">
                          <i class="fas fa-times"></i> Cancel
                        </button>
                      </div>
                    </div>

                    <!-- Edit notes -->
                    <div class="agent-form-step">
                      <div class="agent-form-step-label">
                        <span class="step-num">4</span> Notes <small class="step-hint">(optional)</small>
                      </div>
                      <input v-model="editForm.notes" type="text" class="form-ctrl" placeholder="e.g. Primary broker, contact before Dec 1 renewal" />
                    </div>

                    <div class="contact-form-actions">
                      <button @click="saveEditAgent(link)" class="btn btn-success btn-sm" :disabled="savingAgent">
                        <i v-if="savingAgent" class="fas fa-spinner fa-spin"></i>
                        <i v-else class="fas fa-check"></i>
                        {{ savingAgent ? 'Saving...' : 'Save Changes' }}
                      </button>
                      <button @click="cancelEditAgent()" class="btn btn-secondary btn-sm">Cancel</button>
                    </div>
                  </template>

                </div>
              </div>
              <div v-else-if="!loadingAgents && insurancePersonContacts.length === 0" class="empty-mini">
                No insurance agents or contacts yet
              </div>

              <!-- ── Add Agent inline form ── -->
              <div v-if="showAddAgentForm" class="add-agent-inline">
                <div v-if="addAgentError" class="contact-form-error">{{ addAgentError }}</div>

                <!-- Explainer -->
                <p class="agent-form-explainer">
                  <i class="fas fa-info-circle"></i>
                  An <strong>Insurance Agent</strong> is the agency or brokerage that manages insurance
                  for this entity — e.g. <em>State Farm</em> or <em>ABC Insurance Brokers</em>.
                  You can optionally add the individual person at that agency who is your point of contact.
                </p>

                <!-- ── Step 1: Agency / Broker company ── -->
                <div class="agent-form-step">
                  <div class="agent-form-step-label">
                    <span class="step-num">1</span> Agency / Brokerage <span class="req">*</span>
                  </div>
                  <div class="agent-search-wrap">
                    <input
                      v-model="agentSearchQuery"
                      @input="debouncedAgentSearch"
                      type="text"
                      class="form-ctrl"
                      placeholder="Search by agency name, e.g. State Farm..."
                      autocomplete="off"
                    />
                    <span v-if="searchingAgents" class="search-spinner"><i class="fas fa-spinner fa-spin"></i></span>
                  </div>

                  <!-- Search results dropdown -->
                  <div v-if="agentSearchResults.length > 0 && !selectedAgentEntity" class="agent-search-results">
                    <div
                      v-for="e in agentSearchResults"
                      :key="e.id"
                      class="agent-search-result-item"
                      @click="selectAgentEntity(e)"
                    >
                      <strong>{{ e.name }}</strong>
                      <span class="result-type">{{ e.entity_type }}</span>
                      <small v-if="e.primary_email" class="result-email">{{ e.primary_email }}</small>
                    </div>
                  </div>

                  <!-- "Create new" option when no matches -->
                  <div
                    v-if="agentSearchQuery.length >= 3 && !selectedAgentEntity && !searchingAgents && agentSearchResults.length === 0"
                    class="agent-create-new"
                    @click="createAndSelectAgent"
                  >
                    <i class="fas fa-plus-circle"></i>
                    Create new agency: <strong>"{{ agentSearchQuery }}"</strong>
                  </div>

                  <!-- Selected entity chip -->
                  <div v-if="selectedAgentEntity" class="selected-agent-chip">
                    <i class="fas fa-building"></i>
                    <strong>{{ selectedAgentEntity.name }}</strong>
                    <span class="chip-type">{{ selectedAgentEntity.entity_type || 'AGENCY' }}</span>
                    <button @click="selectedAgentEntity = null; agentSearchQuery = ''" class="chip-clear">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>

                <!-- ── Step 2: Coverage types ── -->
                <div class="agent-form-step">
                  <div class="agent-form-step-label">
                    <span class="step-num">2</span> Which coverage types does this agency handle?
                    <small class="step-hint">(leave blank if they handle everything)</small>
                  </div>
                  <div class="coverage-checkboxes">
                    <label v-for="ct in AGENT_COVERAGE_TYPES" :key="ct.value" class="coverage-checkbox-item">
                      <input type="checkbox" :value="ct.value" v-model="newAgentCoverageTypes" />
                      {{ ct.label }}
                    </label>
                  </div>
                </div>

                <!-- ── Step 3: Individual contact at the agency (optional) ── -->
                <div class="agent-form-step">
                  <div class="agent-form-step-label">
                    <span class="step-num">3</span> Contact person at this agency
                    <small class="step-hint">(optional — the individual producer or account manager)</small>
                  </div>

                  <div v-if="!showAgentContactFields" class="agent-contact-toggle">
                    <button @click="showAgentContactFields = true" class="btn-add-person">
                      <i class="fas fa-user-plus"></i> Add a contact person at {{ selectedAgentEntity ? selectedAgentEntity.name : 'this agency' }}
                    </button>
                  </div>

                  <div v-else class="agent-contact-fields">
                    <div class="contact-form-row">
                      <div class="contact-form-field">
                        <label>Name</label>
                        <input v-model="agentContact.name" type="text" class="form-ctrl" placeholder="e.g. John Smith" />
                      </div>
                      <div class="contact-form-field">
                        <label>Role</label>
                        <select v-model="agentContact.contact_role" class="form-ctrl">
                          <option value="INSURANCE_AGENT">Insurance Agent / Producer</option>
                          <option value="ACCOUNT_MANAGER">Account Manager</option>
                          <option value="CLAIMS_HANDLER">Claims Handler</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>
                    </div>
                    <div class="contact-form-row">
                      <div class="contact-form-field">
                        <label>Email</label>
                        <input
                          v-model="agentContact.email"
                          type="email"
                          class="form-ctrl"
                          placeholder="john@statefarm.com"
                          @blur="checkContactEmail(agentContact.email, selectedAgentEntity && selectedAgentEntity.id, 'agentContactEmailWarning')"
                        />
                        <div v-if="agentContactEmailWarning" :class="['email-warning', agentContactEmailWarning.type === 'duplicate' ? 'email-warning-error' : 'email-warning-caution']">
                          <i :class="agentContactEmailWarning.type === 'duplicate' ? 'fas fa-times-circle' : 'fas fa-exclamation-triangle'"></i>
                          {{ agentContactEmailWarning.message }}
                        </div>
                      </div>
                      <div class="contact-form-field">
                        <label>Phone</label>
                        <input v-model="agentContact.phone" type="tel" class="form-ctrl" placeholder="(555) 123-4567" />
                      </div>
                    </div>
                    <button @click="showAgentContactFields = false; agentContact = { name:'', email:'', phone:'', contact_role:'INSURANCE_AGENT' }" class="btn-text-sm">
                      <i class="fas fa-times"></i> Remove contact person
                    </button>
                  </div>
                </div>

                <!-- ── Notes ── -->
                <div class="agent-form-step">
                  <div class="agent-form-step-label">
                    <span class="step-num">4</span> Notes <small class="step-hint">(optional)</small>
                  </div>
                  <input v-model="newAgentNotes" type="text" class="form-ctrl" placeholder="e.g. Primary broker for renewals, contact before Dec 1" />
                </div>

                <div class="contact-form-actions">
                  <button
                    @click="submitAddAgent"
                    class="btn btn-success btn-sm"
                    :disabled="savingAgent || !selectedAgentEntity"
                  >
                    <i v-if="savingAgent" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-link"></i>
                    {{ savingAgent ? 'Saving...' : 'Save Agent' }}
                  </button>
                  <button
                    @click="showAddAgentForm = false; addAgentError = null; resetAgentForm()"
                    class="btn btn-secondary btn-sm"
                  >Cancel</button>
                </div>
              </div>

            </div>

          </div>
        </div>
        
        <!-- Current Documents Section -->
        <div class="detail-section current-docs-section" id="current-docs">
          <div class="section-header" @click="toggleSection('current')">
            <h4>
              <i class="fas fa-file-alt"></i>
              Current Documents ({{ currentDocuments?.length || 0 }})
            </h4>
            <i :class="['toggle-icon', 'fas', expanded.current ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
          </div>
          <div v-if="expanded.current" class="section-content">
            <div v-if="loadingDocuments" class="loading-mini">
              <i class="fas fa-spinner fa-spin"></i> Loading documents...
            </div>
            <div v-else-if="currentDocuments && currentDocuments.length > 0">
              <div v-for="doc in currentDocuments" :key="doc.id" class="document-row">
                <div class="document-row-main">
                  <strong class="doc-name" :title="doc.name">{{ doc.name }}</strong>
                  <div class="doc-meta-line">
                    <span v-if="getDocumentPeriodLabel(doc)" class="doc-period">
                      {{ getDocumentPeriodLabel(doc) }}
                    </span>
                    <span
                      v-for="cov in getDocumentCoverageLabels(doc)"
                      :key="cov"
                      class="coverage-pill"
                    >{{ cov }}</span>
                    <span v-if="!getDocumentCoverageLabels(doc).length && !getDocumentPeriodLabel(doc)" class="doc-meta-muted">
                      No coverage metadata yet
                    </span>
                  </div>
                  <p class="doc-date">Uploaded {{ formatDate(doc.created_at) }}</p>
                </div>
                <div class="doc-actions">
                  <a :href="getDocumentUrl(doc)" target="_blank" class="btn btn-sm btn-outline">
                    <i class="fas fa-eye"></i> View
                  </a>
                  <button type="button" @click="downloadDocument(doc)" class="btn btn-sm btn-outline">
                    <i class="fas fa-download"></i> Download
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="empty-mini">
              No current documents
            </div>
          </div>
        </div>
        
        <!-- Analysis Sessions Section -->
        <div class="detail-section sessions-section" id="sessions">
          <div class="section-header" @click="toggleSection('sessions')">
            <h4>
              <i class="fas fa-tasks"></i>
              Analysis Sessions ({{ history?.length || 0 }})
            </h4>
            <i :class="['toggle-icon', 'fas', expanded.sessions ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
          </div>
          <div v-if="expanded.sessions" class="section-content">
            <div v-if="loadingHistory" class="loading-mini">
              <i class="fas fa-spinner fa-spin"></i> Loading sessions...
            </div>
            <div v-else-if="history && history.length > 0">
              <div v-for="session in history" :key="session.id" class="session-row">
                <div class="session-row-top">
                  <span class="session-name">{{ getSessionDisplayTitle(session) }}</span>
                  <span class="session-status" :class="getSessionStatusClass(session.status)">
                    {{ formatSessionStatus(session.status) }}
                  </span>
                </div>
                <p class="session-date">{{ formatDate(session.created_at) }}</p>

                <div v-if="session.status === 'PROCESSING'" class="session-processing">
                  <i class="fas fa-spinner fa-spin"></i> Analysis in progress…
                </div>
                <div v-else-if="session.status === 'FAILED'" class="session-failed">
                  <i class="fas fa-exclamation-circle"></i>
                  {{ session.error_message || 'Analysis did not complete successfully.' }}
                </div>
                <template v-else>
                  <p v-if="getSessionResultsSummary(session)?.overview" class="session-summary-text">
                    {{ getSessionResultsSummary(session).overview }}
                  </p>
                  <div class="session-result-meta">
                    <span
                      v-if="getSessionResultsSummary(session)?.total"
                      class="session-counts"
                    >
                      {{ getSessionResultsSummary(session).passed }}/{{ getSessionResultsSummary(session).total }} requirements passed
                      <template v-if="getSessionResultsSummary(session).failed">
                        · {{ getSessionResultsSummary(session).failed }} failed
                      </template>
                    </span>
                    <span
                      v-for="cov in (getSessionResultsSummary(session)?.presentCoverages || [])"
                      :key="cov"
                      class="coverage-pill"
                    >{{ formatCoverageLabel(cov) }}</span>
                  </div>
                  <button
                    type="button"
                    @click="viewAnalysisResults(session)"
                    class="btn btn-sm btn-outline"
                  >
                    <i class="fas fa-chart-bar"></i> View Results
                  </button>
                </template>
              </div>
            </div>
            <div v-else class="empty-mini">
              No analysis sessions yet
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Keep old tab content for now (will remove after testing split view) -->
    <div v-if="false" class="entity-details-old">
      <div class="details-tabs">
        <button 
          :class="{ active: activeTab === 'status' }"
          @click="activeTab = 'status'">
          Compliance Status
        </button>
        <button 
          :class="{ active: activeTab === 'parties' }"
          @click="activeTab = 'parties'; loadParties()">
          Associated Parties
        </button>
        <button 
          :class="{ active: activeTab === 'documents' }"
          @click="activeTab = 'documents'; loadDocuments()">
          Documents
        </button>
        <button 
          :class="{ active: activeTab === 'history' }"
          @click="activeTab = 'history'; loadHistory()">
          Analysis History
        </button>
      </div>
      
      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Compliance Status Tab -->
        <div v-if="activeTab === 'status'" class="status-tab">
          <div v-if="loadingStatus" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i> Loading compliance status...
          </div>
          <div v-else-if="complianceStatus">
            <div class="status-summary">
              <h4>Compliance Summary</h4>
              <p v-if="complianceStatus.all_met">
                ✅ All {{ complianceStatus.total || 0 }} requirements met
              </p>
              <p v-else>
                {{ complianceStatus.passed || 0 }}/{{ complianceStatus.total || 0 }} requirements met
              </p>
              <p class="text-muted">Last checked: {{ formatDate(entity.updated_at) }}</p>
            </div>
            
            <!-- Critical Gaps Only -->
            <div v-if="complianceStatus.critical_gaps && complianceStatus.critical_gaps.length > 0" class="critical-gaps">
              <h4>🚨 Critical Gaps ({{ complianceStatus.critical_gaps.length }})</h4>
              <div v-for="gap in complianceStatus.critical_gaps" :key="gap.id" class="gap-item">
                <span class="gap-name">{{ gap.name }}</span>
                <span class="gap-severity">{{ gap.severity }}</span>
              </div>
            </div>
            
            <button @click="viewAllRequirements" class="btn btn-outline">
              View All {{ complianceStatus.total || 0 }} Requirements →
            </button>
          </div>
          <div v-else class="empty-state">
            No compliance data available
          </div>
        </div>
        
        <!-- Associated Parties Tab -->
        <div v-if="activeTab === 'parties'" class="parties-tab">
          <div v-if="loadingParties" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i> Loading parties...
          </div>
          <div v-else-if="parties">
            <div class="main-entity">
              <h4>Main Entity</h4>
              <p>{{ entity.subject_entity_name }}</p>
            </div>
            
            <div v-if="parties.agents && parties.agents.length > 0" class="agents-section">
              <h4>Agents/Brokers ({{ parties.agents.length }})</h4>
              <div v-for="agent in parties.agents" :key="agent.id" class="party-card">
                <div class="party-info">
                  <strong>{{ agent.name }}</strong>
                  <p v-if="agent.primary_email">📧 {{ agent.primary_email }}</p>
                  <p v-if="agent.primary_phone">☎️ {{ agent.primary_phone }}</p>
                  <p v-if="agent.document_count" class="text-muted">
                    📄 Provided {{ agent.document_count }} documents
                  </p>
                </div>
                <button @click="requestFromAgent(agent)" class="btn btn-sm btn-outline">
                  Request Documents
                </button>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            No associated parties
          </div>
        </div>
        
        <!-- Documents Tab -->
        <div v-if="activeTab === 'documents'" class="documents-tab">
          <div v-if="loadingDocuments" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i> Loading documents...
          </div>
          <div v-else-if="documents && documents.length > 0">
            <h4>Related Documents ({{ documents.length }})</h4>
            <div v-for="doc in documents" :key="doc.id" class="document-item">
              <div class="doc-info">
                <strong>{{ doc.name }}</strong>
                <div class="doc-parties">
                  <span v-for="party in doc.parties" :key="party.id" class="party-badge">
                    {{ party.entity_name }} ({{ party.role }})
                  </span>
                </div>
                <span class="doc-date">{{ formatDate(doc.created_at) }}</span>
              </div>
              <div class="doc-status">
                <span :class="['status-badge', doc.analysis_status]">
                  {{ doc.analysis_status }}
                </span>
                <button v-if="doc.analysis_id" @click="viewAnalysis(doc)" class="btn btn-sm">
                  View Analysis
                </button>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            No documents yet
          </div>
        </div>
        
        <!-- Analysis History Tab -->
        <div v-if="activeTab === 'history'" class="history-tab">
          <div v-if="loadingHistory" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i> Loading history...
          </div>
          <div v-else-if="history && history.length > 0">
            <h4>Recent Analysis ({{ history.length }})</h4>
            <div v-for="item in history" :key="item.id" class="history-item">
              <div class="history-header">
                <span class="document-name">{{ item.document_name }}</span>
                <span class="history-date">{{ formatDate(item.created_at) }}</span>
              </div>
              <div class="history-status">
                <span :class="['status-badge', item.status]">{{ item.status }}</span>
                <button @click="viewAnalysis(item)" class="btn btn-sm">View</button>
              </div>
            </div>
            <button v-if="hasMoreHistory" @click="loadMoreHistory" class="btn btn-outline">
              Load More
            </button>
          </div>
          <div v-else class="empty-state">
            No analysis history
          </div>
        </div>
      </div>
      
      <!-- Entity Actions -->
      <div class="entity-actions">
        <button @click="uploadDocument" class="btn btn-primary">
          <i class="fas fa-upload"></i> Upload Document
        </button>
        <button @click="requestDocuments" class="btn btn-outline">
          <i class="fas fa-envelope"></i> Request Documents
        </button>
        <button @click="collapse" class="btn btn-outline">
          <i class="fas fa-chevron-up"></i> Collapse
        </button>
      </div>
    </div>
  </div>

  <!-- ── Edit Entity Modal ─────────────────────────────────────────────────── -->
  <teleport to="body">
    <div v-if="showEditEntityModal" class="modal-overlay edit-entity-overlay" @click.self="showEditEntityModal = false">
      <div class="modal-dialog edit-entity-dialog">
        <div class="modal-header">
          <h3><i class="fas fa-edit"></i> Edit {{ entityTerm.singular }}</h3>
          <button @click="showEditEntityModal = false" class="btn-close-modal">×</button>
        </div>

        <div class="modal-body">
          <!-- Loading skeleton -->
          <div v-if="editEntityLoading" class="edit-loading">
            <i class="fas fa-spinner fa-spin"></i> Loading details...
          </div>

          <template v-else>
            <div v-if="editEntityError" class="alert alert-danger mb-3">
              <i class="fas fa-exclamation-circle"></i> {{ editEntityError }}
            </div>

            <!-- Name -->
            <div class="form-group">
              <label class="form-label">Name <span class="required-star">*</span></label>
              <input
                v-model="editEntityForm.name"
                type="text"
                class="form-control"
                placeholder="Company or entity name"
              >
            </div>

            <!-- Type -->
            <div class="form-group">
              <label class="form-label">Type</label>
              <select v-model="editEntityForm.entity_type" class="form-control">
                <option value="COMPANY">Company / Franchisee</option>
                <option value="AGENCY">Insurance Agency / Broker</option>
                <option value="PERSON">Individual</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <!-- Contact info -->
            <div class="form-row">
              <div class="form-group col">
                <label class="form-label">Primary Email</label>
                <input
                  v-model="editEntityForm.primary_email"
                  type="email"
                  class="form-control"
                  placeholder="info@company.com"
                >
              </div>
              <div class="form-group col">
                <label class="form-label">Primary Phone</label>
                <input
                  v-model="editEntityForm.primary_phone"
                  type="tel"
                  class="form-control"
                  placeholder="(555) 123-4567"
                >
              </div>
            </div>

            <!-- Address -->
            <AddressFields
              v-model="editEntityForm"
              prefix="edit-entity"
            />
          </template>
        </div>

        <div class="modal-footer">
          <button @click="showEditEntityModal = false" class="btn btn-secondary">
            Cancel
          </button>
          <button
            @click="saveEditEntity"
            class="btn btn-primary"
            :disabled="editEntitySaving || editEntityLoading || !editEntityForm.name.trim()"
          >
            <i v-if="editEntitySaving" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-save"></i>
            {{ editEntitySaving ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import api from '@/services/api';
import { CONTACT_ROLES } from '@/constants';
import { debounce } from 'lodash-es';
import AddressFields from '@/components/shared/AddressFields.vue';

const AGENT_COVERAGE_TYPES = [
  { value: 'GL',    label: 'General Liability (GL)' },
  { value: 'WC',    label: 'Workers Comp (WC)' },
  { value: 'AUTO',  label: 'Auto Liability' },
  { value: 'UMB',   label: 'Umbrella / Excess' },
  { value: 'PL',    label: 'Professional Liability / E&O' },
  { value: 'EPLI',  label: 'Employment Practices (EPLI)' },
  { value: 'CYBER', label: 'Cyber Liability' },
  { value: 'PROP',  label: 'Property' },
  { value: 'CRIME', label: 'Crime / Fidelity' },
  { value: 'LIQUOR', label: 'Liquor Liability' },
  { value: 'DO',    label: 'Directors & Officers' },
  { value: 'OTHER', label: 'Other' },
];

export default {
  name: 'EntityCard',
  components: { AddressFields },
  emits: [
    'viewRequirements',
    'addStore',
    'uploadDocument',
    'requestDocuments',
    'requestFromAgent',
    'viewAnalysis',
    'archiveEntity',
    'entityUpdated',
  ],
  props: {
    entity: {
      type: Object,
      required: true
    },
    entityTerm: {
      type: Object,
      default: () => ({ singular: 'Company', plural: 'Companies' })
    },
    locationTerm: {
      type: Object,
      default: () => ({ singular: 'Location', plural: 'Locations' })
    },
    isHighlighted: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isExpanded: false,
      activeTab: 'status', // Keep for old tab view (will remove)
      // Accordion expansion state
      expanded: {
        locations: true,   // Expanded by default
        agents: true,      // Expanded by default
        current: true,     // Expanded by default (most important!)
        sessions: false,   // Analysis sessions
        history: false     // Collapsed by default
      },
      // Loading states
      loadingStatus: false,
      loadingAgents: false,
      loadingDocuments: false,
      loadingHistory: false,
      loadingLocations: false,
      loadingContacts: false,
      savingContact: false,
      savingAgent: false,
      // Data
      complianceStatus: null,
      subjectAgents: [],
      locations: null,
      documents: null,
      currentDocuments: null,
      historicalDocuments: null,
      history: null,
      hasMoreHistory: false,
      showAllHistory: false,
      // Direct contacts on this entity
      entityContacts: null,
      showAddContactForm: false,
      addContactError: null,
      newContactEmailWarning: null,
      newContact: {
        name: '',
        email: '',
        phone: '',
        contact_role: '',
        title: '',
        is_primary: false
      },
      // Add-agent form state
      showAddAgentForm: false,
      addAgentError: null,
      agentContactEmailWarning: null,
      agentSearchQuery: '',
      agentSearchResults: [],
      searchingAgents: false,
      selectedAgentEntity: null,
      newAgentCoverageTypes: [],
      newAgentNotes: '',
      showAgentContactFields: false,
      agentContact: { name: '', email: '', phone: '', contact_role: 'INSURANCE_AGENT' },
      debouncedAgentSearch: null,
      // Edit-agent form state
      editingAgentLinkId: null,
      editAgentError: null,
      editContactEmailWarning: null,
      editForm: {
        changingAgency: false,
        agencySearchQuery: '',
        agencySearchResults: [],
        searchingAgency: false,
        selectedNewAgency: null,
        coverageTypes: [],
        notes: '',
        showContactFields: false,
        contact: { name: '', email: '', phone: '', contact_role: 'INSURANCE_AGENT' },
      },
      debouncedEditAgencySearch: null,
      debouncedInsuranceAgencySearch: null,
      // Person-first insurance contact form (agency optional)
      showAddInsurancePersonForm: false,
      addInsurancePersonError: null,
      insurancePersonEmailWarning: null,
      newInsurancePerson: {
        name: '',
        email: '',
        phone: '',
        contact_role: 'INSURANCE_AGENT',
        agencySearchQuery: '',
        agencySearchResults: [],
        selectedAgency: null,
        isSearchingAgency: false
      },
      // Contacts keyed by agent_entity_id
      agencyContacts: {},
      // Edit entity form
      showEditEntityModal: false,
      editEntityLoading: false,
      editEntitySaving: false,
      editEntityError: null,
      editEntityForm: {
        name: '',
        entity_type: 'COMPANY',
        primary_email: '',
        primary_phone: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US'
      },
      CONTACT_ROLES,
      AGENT_COVERAGE_TYPES
    };
  },
  created() {
    this.debouncedAgentSearch = debounce(this.searchAgencies, 300);
    this.debouncedEditAgencySearch = debounce(this.searchEditAgency, 300);
    this.debouncedInsuranceAgencySearch = debounce(this.searchInsurancePersonAgency, 300);
  },
  computed: {
    // Insurance-role contacts (agent/broker people) stored directly on the franchisee entity
    // when no agency is known yet. Shown in the Insurance section with a "No Agency Yet" badge.
    insurancePersonContacts() {
      const INSURANCE_ROLES = ['INSURANCE_AGENT', 'ACCOUNT_MANAGER', 'CLAIMS_HANDLER'];
      return (this.entityContacts || []).filter(c => INSURANCE_ROLES.includes(c.contact_role));
    },
    // Store-side staff: owner, GM, billing, etc. — shown in the Staff section.
    storeContacts() {
      const INSURANCE_ROLES = ['INSURANCE_AGENT', 'ACCOUNT_MANAGER', 'CLAIMS_HANDLER'];
      return (this.entityContacts || []).filter(c => !INSURANCE_ROLES.includes(c.contact_role));
    },
    statusClass() {
      const status = this.entity.current_status?.toLowerCase();
      if (status === 'compliant') return 'status-compliant';
      if (status === 'non_compliant' || status === 'action_needed') return 'status-warning';
      if (status === 'partially_compliant') return 'status-partial';
      return 'status-pending';
    },
    statusLabel() {
      return this.entity.current_status_display
        || (this.entity.current_status || '').replace(/_/g, ' ');
    },
    isCompliantStatus() {
      return (this.entity.current_status || '').toUpperCase() === 'COMPLIANT';
    },
    issueHeadline() {
      const fromApi = this.entity.issue_summary?.headline;
      if (fromApi) return fromApi;
      return null;
    },
    recommendedAction() {
      const action = this.entity.issue_summary?.action;
      if (action) return action;
      const rec = this.entity.agent_recommendation;
      if (rec?.action) {
        return String(rec.action).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      }
      return null;
    },
    nextExpiryLine() {
      const next = this.entity.issue_summary?.next_expiry;
      if (!next?.date) return null;
      const label = this.formatCoverageLabel(next.coverage);
      const days = next.days;
      if (typeof days === 'number') {
        if (days < 0) return `${label} expired ${Math.abs(days)} days ago`;
        if (days === 0) return `${label} expires today`;
        return `${label} expires in ${days} days`;
      }
      return `${label} expires ${next.date}`;
    },
    outreachLine() {
      const o = this.entity.issue_summary?.outreach;
      if (!o?.sent_at) return null;
      const when = new Date(o.sent_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const who = o.needs_agent_handoff ? 'franchisee (to forward)' : (o.route === 'agent' ? 'agent' : 'contact');
      return `${o.kind} to ${who} ${when} · ${o.state_label}`;
    },
    outreachProblem() {
      return ['bounced', 'complaint', 'failed'].includes(this.entity.issue_summary?.outreach?.state);
    },
    locationCountHint() {
      if (this.locations?.length) {
        return `${this.locations.length} ${this.locations.length === 1 ? this.locationTerm.singular.toLowerCase() : this.locationTerm.plural.toLowerCase()}`;
      }
      return null;
    },
    locationCountTitle() {
      return `Locations may be covered individually — expand to see coverage status when known`;
    },
    progressClass() {
      const pct = this.entity.compliance_percentage || 0;
      if (pct >= 90) return 'progress-success';
      if (pct >= 70) return 'progress-warning';
      return 'progress-danger';
    },
    expiringCount() {
      if (!this.currentDocuments) return 0;
      return this.currentDocuments.filter(doc => this.isExpiringSoon(doc)).length;
    },
    continuityChipLabel() {
      const summary = this.entity.continuity_summary;
      if (!summary) return null;
      if (!summary.has_interval_data) return 'No coverage history';
      const pct = Math.round(summary.pct_covered ?? 0);
      if (summary.lapse_count > 0) {
        const lapseWord = summary.lapse_count === 1 ? 'lapse' : 'lapses';
        return `${pct}% covered · ${summary.lapse_count} ${lapseWord}`;
      }
      return `${pct}% covered`;
    },
    continuityChipClass() {
      const summary = this.entity.continuity_summary;
      if (!summary?.has_interval_data) return 'continuity-chip-neutral';
      if (summary.lapse_count > 0) return 'continuity-chip-warning';
      return 'continuity-chip-ok';
    },
    continuityChipTitle() {
      const summary = this.entity.continuity_summary;
      if (!summary?.has_interval_data) {
        return 'No coverage intervals yet — open record after analyzing documents';
      }
      return 'View coverage history';
    },
    continuityDefaultTab() {
      const summary = this.entity.continuity_summary;
      if (summary?.has_interval_data) return 'continuity';
      return undefined;
    },
    continuityMiniBarClass() {
      const summary = this.entity.continuity_summary;
      if (!summary?.has_interval_data) return '';
      if (summary.lapse_count > 0) return 'bar-warning';
      if ((summary.pct_covered || 0) >= 90) return 'bar-ok';
      return 'bar-mixed';
    },
  },
  methods: {
    // ── Edit Entity ──────────────────────────────────────────────────────────
    async openEditEntity() {
      const entityId = this.entity.subject_entity;
      this.showEditEntityModal = true;
      this.editEntityLoading = true;
      this.editEntityError = null;
      try {
        const response = await api.get(`/entities/${entityId}/`);
        const e = response.data;
        this.editEntityForm = {
          name: e.name || '',
          entity_type: e.entity_type || 'COMPANY',
          primary_email: e.primary_email || '',
          primary_phone: e.primary_phone || '',
          address_line1: e.address_line1 || '',
          address_line2: e.address_line2 || '',
          city: e.city || '',
          state: e.state || '',
          postal_code: e.postal_code || '',
          country: e.country || 'US'
        };
      } catch (err) {
        this.editEntityError = 'Failed to load entity details. Please try again.';
      } finally {
        this.editEntityLoading = false;
      }
    },

    async saveEditEntity() {
      if (!this.editEntityForm.name.trim()) {
        this.editEntityError = 'Name is required.';
        return;
      }
      const entityId = this.entity.subject_entity;
      this.editEntitySaving = true;
      this.editEntityError = null;
      try {
        await api.patch(`/entities/${entityId}/`, this.editEntityForm);
        this.showEditEntityModal = false;
        this.$emit('entity-updated', { id: entityId, name: this.editEntityForm.name });
      } catch (err) {
        this.editEntityError = 'Failed to save changes. Please try again.';
        console.error('Error updating entity:', err);
      } finally {
        this.editEntitySaving = false;
      }
    },

    toggleExpand() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded) {
        if (!this.complianceStatus) this.loadStatus();
        if (this.subjectAgents.length === 0) this.loadAgents();
        if (!this.documents) this.loadDocuments();
        if (!this.locations) this.loadLocations();
        if (!this.history) this.loadHistory();
        if (!this.entityContacts) this.loadContacts();
      }
    },
    
    collapse() {
      this.isExpanded = false;
    },
    
    toggleSection(section) {
      this.expanded[section] = !this.expanded[section];
    },
    
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    },
    
    async loadStatus() {
      this.loadingStatus = true;
      try {
        const response = await api.get(`/compliance-subjects/${this.entity.id}/detailed_status/`);
        const requirements = response.data || [];
        
        // Calculate summary from requirements array
        const passed = requirements.filter(r => {
          const result = r.latest_result;
          return result && (result.passed === true || result.result === 'PASS');
        }).length;
        
        const failed = requirements.filter(r => {
          const result = r.latest_result;
          return result && (result.passed === false || result.result === 'FAIL');
        }).length;
        
        this.complianceStatus = {
          requirements: requirements,
          passed: passed,
          failed: failed,
          total: requirements.length,
          all_met: passed === requirements.length && requirements.length > 0
        };
        
        console.log(`Compliance status: ${passed}/${requirements.length} requirements met`);
      } catch (error) {
        console.error('Error loading compliance status:', error);
        this.complianceStatus = null;
      } finally {
        this.loadingStatus = false;
      }
    },
    
    async loadAgents() {
      this.loadingAgents = true;
      try {
        const response = await api.get(`/compliance-subjects/${this.entity.id}/agents/`);
        this.subjectAgents = response.data.agents || [];
        // Load contacts for each agency in parallel (bust cache first)
        this.agencyContacts = {};
        await Promise.all(this.subjectAgents.map(link => this.loadAgencyContacts(link.agent_entity_id)));
      } catch (error) {
        console.error('Error loading agents:', error);
        this.subjectAgents = [];
      } finally {
        this.loadingAgents = false;
      }
    },

    async searchAgencies() {
      if (this.agentSearchQuery.length < 2) {
        this.agentSearchResults = [];
        return;
      }
      this.searchingAgents = true;
      try {
        const response = await api.get('/entities/', {
          params: { search: this.agentSearchQuery, limit: 8 }
        });
        const data = response.data;
        this.agentSearchResults = Array.isArray(data) ? data : (data.results || []);
      } catch (err) {
        console.error('Agency search error:', err);
        this.agentSearchResults = [];
      } finally {
        this.searchingAgents = false;
      }
    },

    selectAgentEntity(entity) {
      this.selectedAgentEntity = entity;
      this.agentSearchQuery = entity.name;
      this.agentSearchResults = [];
    },

    async createAndSelectAgent() {
      this.savingAgent = true;
      try {
        const response = await api.post('/entities/', {
          name: this.agentSearchQuery.trim(),
          entity_type: 'AGENCY',
        });
        this.selectAgentEntity(response.data);
      } catch (err) {
        this.addAgentError = 'Failed to create agency. Please try again.';
      } finally {
        this.savingAgent = false;
      }
    },

    async submitAddAgent() {
      if (!this.selectedAgentEntity) {
        this.addAgentError = 'Please search for and select an agency above, or use "Create new agency" to add one.';
        return;
      }
      this.savingAgent = true;
      this.addAgentError = null;
      try {
        await api.post(`/compliance-subjects/${this.entity.id}/agents/`, {
          agent_entity_id: this.selectedAgentEntity.id,
          coverage_types: this.newAgentCoverageTypes,
          notes: this.newAgentNotes,
        });

        // Optionally create the contact person AT the agency entity
        if (this.showAgentContactFields && this.agentContact.name && this.agentContact.email) {
          await api.post('/contacts/', {
            entity: this.selectedAgentEntity.id,
            name: this.agentContact.name,
            email: this.agentContact.email,
            phone: this.agentContact.phone || '',
            contact_role: this.agentContact.contact_role,
          });
        }

        this.showAddAgentForm = false;
        this.resetAgentForm();
        await this.loadAgents();
      } catch (err) {
        const d = err.response?.data;
        this.addAgentError = d?.error || d?.detail || 'Failed to save agent. Please try again.';
      } finally {
        this.savingAgent = false;
      }
    },

    async removeAgent(linkId) {
      if (!confirm('Remove this agent link?')) return;
      try {
        await api.delete(`/compliance-subjects/${this.entity.id}/agents/${linkId}/`);
        await this.loadAgents();
      } catch (err) {
        console.error('Error removing agent:', err);
      }
    },

    resetAgentForm() {
      this.agentSearchQuery = '';
      this.agentSearchResults = [];
      this.selectedAgentEntity = null;
      this.newAgentCoverageTypes = [];
      this.newAgentNotes = '';
      this.showAgentContactFields = false;
      this.agentContact = { name: '', email: '', phone: '', contact_role: 'INSURANCE_AGENT' };
      this.agentContactEmailWarning = null;
    },

    // ── Person-first Insurance Contact form (agency optional) ─────────────────
    resetInsurancePerson() {
      this.newInsurancePerson = {
        name: '',
        email: '',
        phone: '',
        contact_role: 'INSURANCE_AGENT',
        agencySearchQuery: '',
        agencySearchResults: [],
        selectedAgency: null,
        isSearchingAgency: false
      };
      this.insurancePersonEmailWarning = null;
      this.addInsurancePersonError = null;
    },

    async searchInsurancePersonAgency() {
      const q = this.newInsurancePerson.agencySearchQuery;
      if (!q || q.length < 2) { this.newInsurancePerson.agencySearchResults = []; return; }
      this.newInsurancePerson.isSearchingAgency = true;
      try {
        const r = await api.get('/entities/', { params: { search: q, limit: 8 } });
        const d = r.data;
        this.newInsurancePerson.agencySearchResults = Array.isArray(d) ? d : (d.results || []);
      } catch (e) {
        this.newInsurancePerson.agencySearchResults = [];
      } finally {
        this.newInsurancePerson.isSearchingAgency = false;
      }
    },

    async createInsurancePersonAgency() {
      const name = this.newInsurancePerson.agencySearchQuery.trim();
      if (!name) return;
      try {
        const r = await api.post('/entities/', { name, entity_type: 'AGENCY' });
        this.newInsurancePerson.selectedAgency = r.data;
        this.newInsurancePerson.agencySearchQuery = r.data.name;
        this.newInsurancePerson.agencySearchResults = [];
      } catch (e) {
        console.error('Error creating agency:', e);
      }
    },

    async submitAddInsurancePerson() {
      const p = this.newInsurancePerson;
      if (!p.name.trim() || !p.email.trim()) {
        this.addInsurancePersonError = 'Name and email are required.';
        return;
      }

      this.savingAgent = true;
      this.addInsurancePersonError = null;

      try {
        const entityId = this.entity.subject_entity_id || this.entity.subject_entity;

        if (p.selectedAgency) {
          // Agency known: create ComplianceSubjectAgent + Contact on agency entity
          await api.post(`/compliance-subjects/${this.entity.id}/agents/`, {
            agent_entity_id: p.selectedAgency.id,
            coverage_types: [],
            notes: ''
          });
          await api.post('/contacts/', {
            entity: p.selectedAgency.id,
            name: p.name.trim(),
            email: p.email.trim(),
            phone: p.phone || '',
            contact_role: p.contact_role
          });
        } else {
          // No agency yet: save as Contact directly on franchisee entity
          await api.post('/contacts/', {
            entity: entityId,
            name: p.name.trim(),
            email: p.email.trim(),
            phone: p.phone || '',
            contact_role: p.contact_role
          });
        }

        this.showAddInsurancePersonForm = false;
        this.resetInsurancePerson();
        await this.loadAgents();
        await this.loadContacts();
      } catch (err) {
        const d = err.response?.data;
        this.addInsurancePersonError = d?.error || d?.detail || 'Failed to save. Please try again.';
      } finally {
        this.savingAgent = false;
      }
    },

    resetContactObj() {
      return { name: '', email: '', phone: '', contact_role: 'INSURANCE_AGENT' };
    },

    // ── Load contacts keyed by agency entity id ──────────────────────────────
    async loadAgencyContacts(agentEntityId) {
      if (this.agencyContacts[agentEntityId]) return; // cached
      try {
        const response = await api.get('/contacts/', { params: { entity: agentEntityId } });
        const data = response.data;
        this.agencyContacts = {
          ...this.agencyContacts,
          [agentEntityId]: Array.isArray(data) ? data : (data.results || []),
        };
      } catch (err) {
        console.error('Error loading agency contacts:', err);
        this.agencyContacts = { ...this.agencyContacts, [agentEntityId]: [] };
      }
    },

    async deleteAgencyContact(contactId, agentEntityId) {
      if (!confirm('Remove this contact?')) return;
      try {
        await api.delete(`/contacts/${contactId}/`);
        // Bust cache so it reloads
        const updated = { ...this.agencyContacts };
        delete updated[agentEntityId];
        this.agencyContacts = updated;
        await this.loadAgencyContacts(agentEntityId);
      } catch (err) {
        console.error('Error deleting agency contact:', err);
      }
    },

    // ── Edit agent link ───────────────────────────────────────────────────────
    async startEditAgent(link) {
      this.showAddAgentForm = false;
      this.editingAgentLinkId = link.id;
      this.editAgentError = null;
      this.editForm = {
        changingAgency: false,
        agencySearchQuery: '',
        agencySearchResults: [],
        searchingAgency: false,
        selectedNewAgency: null,
        coverageTypes: [...(link.coverage_types || [])],
        notes: link.notes || '',
        showContactFields: false,
        contact: this.resetContactObj(),
      };
      // Load contacts for this agency
      await this.loadAgencyContacts(link.agent_entity_id);
    },

    cancelEditAgent() {
      this.editingAgentLinkId = null;
      this.editAgentError = null;
      this.editContactEmailWarning = null;
    },

    async searchEditAgency() {
      if (this.editForm.agencySearchQuery.length < 2) {
        this.editForm.agencySearchResults = [];
        return;
      }
      this.editForm.searchingAgency = true;
      try {
        const response = await api.get('/entities/', {
          params: { search: this.editForm.agencySearchQuery, limit: 8 }
        });
        const data = response.data;
        this.editForm.agencySearchResults = Array.isArray(data) ? data : (data.results || []);
      } catch (err) {
        this.editForm.agencySearchResults = [];
      } finally {
        this.editForm.searchingAgency = false;
      }
    },

    async createAndSelectEditAgency() {
      this.savingAgent = true;
      try {
        const response = await api.post('/entities/', {
          name: this.editForm.agencySearchQuery.trim(),
          entity_type: 'AGENCY',
        });
        this.editForm.selectedNewAgency = response.data;
        this.editForm.agencySearchQuery = response.data.name;
        this.editForm.agencySearchResults = [];
      } catch (err) {
        this.editAgentError = 'Could not create agency. Please try again.';
      } finally {
        this.savingAgent = false;
      }
    },

    async saveEditAgent(link) {
      this.savingAgent = true;
      this.editAgentError = null;
      try {
        // If changing the agency: delete old link, create new one
        if (this.editForm.changingAgency && this.editForm.selectedNewAgency) {
          await api.delete(`/compliance-subjects/${this.entity.id}/agents/${link.id}/`);
          await api.post(`/compliance-subjects/${this.entity.id}/agents/`, {
            agent_entity_id: this.editForm.selectedNewAgency.id,
            coverage_types: this.editForm.coverageTypes,
            notes: this.editForm.notes,
          });
        } else {
          // Just update coverage types + notes on existing link
          await api.post(`/compliance-subjects/${this.entity.id}/agents/`, {
            agent_entity_id: link.agent_entity_id,
            coverage_types: this.editForm.coverageTypes,
            notes: this.editForm.notes,
          });
        }

        // Optionally save new contact at agency
        const targetEntityId = (this.editForm.changingAgency && this.editForm.selectedNewAgency)
          ? this.editForm.selectedNewAgency.id
          : link.agent_entity_id;

        if (this.editForm.showContactFields && this.editForm.contact.name && this.editForm.contact.email) {
          await api.post('/contacts/', {
            entity: targetEntityId,
            name: this.editForm.contact.name,
            email: this.editForm.contact.email,
            phone: this.editForm.contact.phone || '',
            contact_role: this.editForm.contact.contact_role,
          });
          // Bust contact cache for this agency
          const updated = { ...this.agencyContacts };
          delete updated[targetEntityId];
          this.agencyContacts = updated;
        }

        this.editingAgentLinkId = null;
        await this.loadAgents();
        // Reload contacts for updated agency
        await this.loadAgencyContacts(targetEntityId);
      } catch (err) {
        const d = err.response?.data;
        this.editAgentError = d?.error || d?.detail || 'Failed to save changes. Please try again.';
      } finally {
        this.savingAgent = false;
      }
    },
    
    async loadContacts() {
      const entityId = this.entity.subject_entity_id || this.entity.subject_entity;
      if (!entityId) return;
      this.loadingContacts = true;
      try {
        const response = await api.get('/contacts/', { params: { entity: entityId } });
        const data = response.data;
        this.entityContacts = Array.isArray(data) ? data : (data.results || []);
      } catch (error) {
        console.error('Error loading contacts:', error);
        this.entityContacts = [];
      } finally {
        this.loadingContacts = false;
      }
    },

    // ── Email duplicate detection ─────────────────────────────────────────────
    async checkContactEmail(email, currentEntityId, warningField) {
      this[warningField] = null;
      if (!email || !email.includes('@')) return;
      try {
        const response = await api.get('/contacts/', { params: { email } });
        const results = Array.isArray(response.data) ? response.data : (response.data.results || []);
        if (results.length === 0) return;

        // Check if any match is on a different entity
        const elsewhere = results.filter(c => String(c.entity) !== String(currentEntityId));
        const sameEntity = results.filter(c => String(c.entity) === String(currentEntityId));

        if (sameEntity.length > 0) {
          this[warningField] = {
            type: 'duplicate',
            message: `This email is already saved as a contact (${sameEntity[0].name}) for this entity.`
          };
        } else if (elsewhere.length > 0) {
          const names = [...new Set(elsewhere.map(c => c.entity_name || c.name))].slice(0, 2).join(', ');
          this[warningField] = {
            type: 'other_entity',
            message: `This email is already associated with a contact at: ${names}. You can still save if this is intentional.`
          };
        }
      } catch (e) {
        // Non-blocking — ignore errors
      }
    },

    async submitAddContact() {
      const entityId = this.entity.subject_entity_id || this.entity.subject_entity;
      if (!this.newContact.name || !this.newContact.email || !this.newContact.contact_role) return;
      this.savingContact = true;
      this.addContactError = null;
      try {
        await api.post('/contacts/', { ...this.newContact, entity: entityId });
        this.showAddContactForm = false;
        this.newContactEmailWarning = null;
        this.newContact = { name: '', email: '', phone: '', contact_role: '', title: '', is_primary: false };
        await this.loadContacts();
      } catch (err) {
        const d = err.response?.data;
        if (typeof d === 'string') this.addContactError = d;
        else if (d?.detail) this.addContactError = d.detail;
        else this.addContactError = 'Failed to save contact. Please try again.';
      } finally {
        this.savingContact = false;
      }
    },

    async deleteContact(contactId) {
      if (!confirm('Remove this contact?')) return;
      try {
        await api.delete(`/contacts/${contactId}/`);
        await this.loadContacts();
      } catch (err) {
        console.error('Error deleting contact:', err);
      }
    },

    async loadLocations() {
      if (this.locations) return; // Already loaded
      
      // Check if we have entity ID
      const entityId = this.entity.subject_entity_id || this.entity.subject_entity;
      if (!entityId) {
        console.warn('No entity ID available for loading locations');
        this.locations = [];
        return;
      }
      
      this.loadingLocations = true;
      try {
        // Get locations for this entity
        const response = await api.get(`/entities/${entityId}/locations/`);
        this.locations = response.data.locations || response.data.results || response.data || [];
        console.log(`Loaded ${this.locations.length} locations for entity ${entityId}`);
      } catch (error) {
        console.error('Error loading locations:', error);
        this.locations = [];
      } finally {
        this.loadingLocations = false;
      }
    },
    
    async loadDocuments() {
      if (this.documents) return; // Already loaded
      
      this.loadingDocuments = true;
      try {
        // Get documents linked to this subject via DocumentParty
        const response = await api.get(`/compliance-subjects/${this.entity.id}/documents/`);
        this.documents = response.data.documents || [];
        
        // Split into current vs historical
        const now = new Date();
        
        this.currentDocuments = this.documents.filter(doc => {
          if (!doc.expiration_date) return true; // No expiration = current
          const expDate = new Date(doc.expiration_date);
          return expDate >= now; // Not expired
        }).sort((a, b) => {
          // Sort by expiration (soonest first)
          if (!a.expiration_date) return 1;
          if (!b.expiration_date) return -1;
          return new Date(a.expiration_date) - new Date(b.expiration_date);
        });
        
        this.historicalDocuments = this.documents.filter(doc => {
          if (!doc.expiration_date) return false;
          const expDate = new Date(doc.expiration_date);
          return expDate < now; // Expired
        }).sort((a, b) => {
          // Sort by date (newest first)
          return new Date(b.created_at) - new Date(a.created_at);
        });
      } catch (error) {
        console.error('Error loading documents:', error);
        this.documents = [];
        this.currentDocuments = [];
        this.historicalDocuments = [];
      } finally {
        this.loadingDocuments = false;
      }
    },
    
    async loadHistory() {
      if (this.history) return; // Already loaded
      
      this.loadingHistory = true;
      try {
        // Get analysis sessions
        const response = await api.get(`/compliance-subjects/${this.entity.id}/analysis-sessions/`);
        
        // Store sessions directly (don't flatten to analyses)
        this.history = response.data.sessions || response.data || [];
        
        this.hasMoreHistory = false;
      } catch (error) {
        console.error('Error loading history:', error);
        this.history = [];
      } finally {
        this.loadingHistory = false;
      }
    },
    
    async loadMoreHistory() {
      this.openEntityRecord('timeline');
    },

    openEntityRecord(tab) {
      if (this.entityTerm?.singular === 'Patient') {
        this.$router.push({
          name: 'LearningPatientChart',
          params: { subjectId: String(this.entity.id) },
        });
        return;
      }
      const projectId = this.entity.project ?? this.$route?.params?.id;
      if (!projectId) return;
      let resolvedTab = tab;
      if (!resolvedTab) {
        const summary = this.entity.continuity_summary;
        if (summary?.lapse_count > 0) {
          resolvedTab = 'continuity';
        }
      }
      this.$router.push({
        name: 'EntityRecord',
        params: { projectId, subjectId: this.entity.id },
        query: resolvedTab ? { tab: resolvedTab } : {},
      });
    },
    
    viewAllRequirements() {
      this.$emit('view-requirements', this.entity);
    },
    
    addStore() {
      this.$emit('add-store', this.entity);
    },
    
    uploadDocument() {
      this.$emit('upload-document', this.entity);
    },
    
    requestDocuments() {
      this.$emit('request-documents', this.entity);
    },
    
    requestFromAgent(agent) {
      this.$emit('request-from-agent', { entity: this.entity, agent });
    },

    requestFromAgentLink(link) {
      this.$emit('request-from-agent', {
        entity: this.entity,
        agent: {
          id: link.agent_entity_id,
          name: link.agent_entity_name,
          primary_email: link.agent_entity_email,
          primary_phone: link.agent_entity_phone,
        }
      });
    },
    
    viewAnalysis(item) {
      this.$emit('view-analysis', item);
    },
    
    confirmArchive() {
      const entityName = this.entity.subject_entity_name || 'this entity';
      if (confirm(`Are you sure you want to archive "${entityName}"? This will remove it from the active list but preserve historical data.`)) {
        this.$emit('archive-entity', this.entity);
      }
    },
    
    isExpiringSoon(doc) {
      if (!doc.expiration_date) return false;
      const expDate = new Date(doc.expiration_date);
      const now = new Date();
      const ninetyDaysFromNow = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
      return expDate < ninetyDaysFromNow && expDate >= now;
    },
    
    getLocationStatusClass(location) {
      const status = location.compliance_status?.toLowerCase();
      if (status === 'compliant') return 'status-compliant';
      if (status === 'non_compliant' || status === 'action_needed') return 'status-warning';
      return 'status-pending';
    },
    
    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const now = new Date();
      const diffTime = Math.abs(now - d);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return d.toLocaleDateString();
    },
    
    getSessionStatusClass(status) {
      const statusMap = {
        'PROCESSING': 'status-processing',
        'COMPLETED': 'status-completed',
        'FAILED': 'status-failed',
        'DRAFT': 'status-draft'
      };
      return statusMap[status] || 'status-pending';
    },
    
    getSessionStatusIcon(status) {
      const iconMap = {
        'PROCESSING': 'fas fa-spinner fa-spin',
        'COMPLETED': 'fas fa-check-circle',
        'FAILED': 'fas fa-exclamation-circle',
        'DRAFT': 'fas fa-file-alt'
      };
      return iconMap[status] || 'fas fa-clock';
    },

    getResolvedCheckRun(session) {
      if (session?.check_runs?.length) {
        return session.check_runs[0];
      }

      const analysis = session?.analyses?.[0];
      if (!analysis) return null;

      const runId = analysis.comprehensive_check_run_id
        || analysis.results?.comprehensive_check_run_id;
      if (!runId) return null;

      return {
        id: runId,
        document_id: analysis.document_id ?? analysis.document,
        status: analysis.status,
        results_summary: analysis.results?.results_summary,
      };
    },

    getPrimaryCheckRun(session) {
      return this.getResolvedCheckRun(session);
    },

    normalizeCheckRunForEmit(checkRun) {
      if (!checkRun?.id) return null;
      return {
        id: checkRun.id,
        document_id: checkRun.document_id ?? checkRun.document?.id,
        status: checkRun.status,
        results_summary: checkRun.results_summary,
      };
    },

    emitViewAnalysis(checkRun, session) {
      const run = this.normalizeCheckRunForEmit(checkRun);
      if (!run?.id) return;

      this.$emit('view-analysis', {
        run,
        subject: this.entity,
        session,
      });
    },

    getSessionDocumentLabel(session) {
      const batchMatch = session?.name?.match(/Batch Analysis:\s*(.+?)\s*\(Subject:/i);
      if (batchMatch) {
        return batchMatch[1].trim();
      }

      const analysis = session?.analyses?.[0];
      if (analysis?.document?.name) return analysis.document.name;
      if (analysis?.document_name) return analysis.document_name;

      const genericMatch = session?.name?.match(/Analysis:\s*(.+?)(?:\s*\(|$)/i);
      if (genericMatch) return genericMatch[1].trim();

      return null;
    },

    getSessionDisplayTitle(session) {
      const documentLabel = this.getSessionDocumentLabel(session);
      if (documentLabel) return documentLabel;
      return session?.name || 'Analysis session';
    },

    formatSessionStatus(status) {
      if (!status) return '';
      const s = String(status).toUpperCase();
      if (s === 'COMPLETED') return 'Completed';
      if (s === 'PROCESSING') return 'Processing';
      if (s === 'FAILED') return 'Failed';
      return String(status).replace(/_/g, ' ');
    },

    formatCoverageLabel(raw) {
      if (!raw) return '';
      const key = String(raw).toUpperCase().replace(/\s+/g, '_');
      const map = {
        WORKERS_COMP: 'WC',
        WORKERS_COMPENSATION: 'WC',
        GENERAL_LIABILITY: 'GL',
        AUTO_LIABILITY: 'Auto',
        UMBRELLA: 'Umbrella',
        EXCESS: 'Excess',
        PROFESSIONAL_LIABILITY: 'PL',
        CYBER: 'Cyber',
        LIQUOR_LIABILITY: 'Liquor',
        PROPERTY: 'Property',
        GL: 'GL',
        WC: 'WC',
        AUTO: 'Auto',
        UMB: 'Umbrella',
        PL: 'PL',
      };
      if (map[key]) return map[key];
      return String(raw)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    },

    getDocumentCoverageLabels(doc) {
      const types = Array.isArray(doc?.coverage_types) ? doc.coverage_types : [];
      const fromMeta = doc?.coverage_metadata && typeof doc.coverage_metadata === 'object'
        ? Object.keys(doc.coverage_metadata)
        : [];
      const merged = [...new Set([...types, ...fromMeta].filter(Boolean))];
      return merged.map(t => this.formatCoverageLabel(t));
    },

    getDocumentPeriodLabel(doc) {
      const meta = doc?.coverage_metadata;
      if (!meta || typeof meta !== 'object') {
        if (doc?.expiration_date) {
          return `Expires ${this.formatDate(doc.expiration_date)}`;
        }
        return null;
      }
      let earliest = null;
      let latest = null;
      Object.values(meta).forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;
        const eff = entry.effective_date || entry.effective;
        const exp = entry.expiration_date || entry.expiration;
        if (eff) {
          const d = String(eff).slice(0, 10);
          if (!earliest || d < earliest) earliest = d;
        }
        if (exp) {
          const d = String(exp).slice(0, 10);
          if (!latest || d > latest) latest = d;
        }
      });
      if (earliest && latest) {
        const y1 = earliest.slice(0, 4);
        const y2 = latest.slice(0, 4);
        if (y1 === y2) return `${y1} coverage`;
        return `${y1}–${y2} coverage`;
      }
      if (latest) return `Through ${latest.slice(0, 4)}`;
      if (earliest) return `From ${earliest.slice(0, 4)}`;
      return null;
    },

    locationCoverageStatus(location) {
      // Prefer explicit fields when present; metadata may carry JMFS / FranConnect flags later
      const raw = (
        location?.coverage_status
        || location?.compliance_status
        || location?.metadata?.coverage_status
        || location?.metadata?.insured_status
        || ''
      ).toString().toLowerCase();
      if (['insured', 'covered', 'compliant', 'on_certificate'].includes(raw)) return 'covered';
      if (['uninsured', 'not_covered', 'gap', 'missing', 'non_compliant'].includes(raw)) return 'gap';
      if (['partial', 'partially_compliant', 'some'].includes(raw)) return 'partial';
      return 'unknown';
    },

    locationCoverageClass(location) {
      return `loc-cov-${this.locationCoverageStatus(location)}`;
    },

    locationCoverageLabel(location) {
      const s = this.locationCoverageStatus(location);
      if (s === 'covered') return 'Covered';
      if (s === 'gap') return 'Gap';
      if (s === 'partial') return 'Partial';
      return 'Unknown';
    },

    locationCoverageTitle(location) {
      const addr = [location.address_line1, location.city, location.state].filter(Boolean).join(', ');
      const status = this.locationCoverageLabel(location);
      const base = addr || 'No address';
      return `${base} · Certificate coverage: ${status}`;
    },

    getSessionResultsSummary(session) {
      const run = this.getPrimaryCheckRun(session);
      const summary = run?.results_summary;
      if (!summary || typeof summary !== 'object') return null;

      const passed = summary.passed ?? summary.passed_count ?? 0;
      const failed = summary.failed ?? summary.failed_count ?? 0;
      const total = summary.total ?? (passed + failed);

      return {
        overview: (summary.agent_overview || summary.summary || '').trim(),
        presentCoverages: summary.present_coverages || [],
        passed,
        failed,
        total,
      };
    },

    getSessionOutcomeHeadline(session) {
      const run = this.getPrimaryCheckRun(session);
      if (run?.status === 'FAILED') {
        return 'Analysis encountered issues';
      }
      if (session.status === 'COMPLETED') {
        return 'Analysis completed successfully';
      }
      return 'Analysis recorded';
    },
    
    async viewAnalysisResults(session) {
      const resolved = this.getResolvedCheckRun(session);
      if (resolved?.id && (resolved.document_id || resolved.document?.id)) {
        this.emitViewAnalysis(resolved, session);
        return;
      }

      try {
        let checkRuns = [];

        if (session?.id) {
          const response = await api.get('/comprehensive-check-runs/', {
            params: {
              session_id: session.id,
              ordering: '-created_at',
            },
          });
          checkRuns = response.data.results || response.data || [];
        }

        if ((!checkRuns || checkRuns.length === 0) && resolved?.id) {
          const runResponse = await api.get(`/comprehensive-check-runs/${resolved.id}/`);
          checkRuns = [runResponse.data];
        }

        if ((!checkRuns || checkRuns.length === 0) && session?.name) {
          const docMatch = session.name.match(/([a-f0-9]+)\.pdf/i);
          const projectId = this.entity.project ?? this.$route?.params?.id;

          if (docMatch && projectId) {
            const docsResponse = await api.get(`/projects/${projectId}/documents/`, {
              params: { search: docMatch[1] },
            });
            const docs = docsResponse.data.results || docsResponse.data || [];

            if (docs.length > 0) {
              const response = await api.get('/comprehensive-check-runs/', {
                params: {
                  document_id: docs[0].id,
                  session_id: session.id,
                  ordering: '-created_at',
                },
              });
              checkRuns = response.data.results || response.data || [];
            }
          }
        }

        if (checkRuns?.length) {
          this.emitViewAnalysis(checkRuns[0], session);
        } else {
          alert('No analysis results found for this session.');
        }
      } catch (error) {
        console.error('Error fetching check runs:', error);
        alert('Error loading analysis results. Please try again.');
      }
    },
    
    getDocumentUrl(doc) {
      // Use unified document URL helper
      const { getDocumentUrl } = require('@/utils/documentUrlHelper');
      return getDocumentUrl(doc);
    },
    
    downloadDocument(doc) {
      if (!doc || !doc.id) return;
      
      // For S3 files, we can use the presigned URL directly for downloads
      // Presigned URLs don't require authentication and work for both viewing and downloading
      if (doc.file_url && doc.file_url.startsWith('http')) {
        console.log('Using presigned URL for download:', doc.name);
        
        // Create a download link with the presigned URL
        // The browser will handle the download based on Content-Disposition header
        const link = document.createElement('a');
        link.href = doc.file_url;
        link.setAttribute('download', doc.name || 'document.pdf');
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        console.log('✅ Document download initiated:', doc.name);
      } else {
        // Fallback: Use the authenticated download endpoint
        // This is needed for local development or if presigned URL is not available
        console.log('Using authenticated download endpoint');
        const { getDocumentDownloadUrl } = require('@/utils/documentUrlHelper');
        const downloadUrl = getDocumentDownloadUrl(doc);
        
        if (downloadUrl && downloadUrl !== '#') {
          window.open(downloadUrl, '_blank');
        } else {
          console.error('Could not generate download URL for:', doc);
          alert('Failed to download document. Please try again.');
        }
      }
    }
  }
};
</script>

<style scoped>
.entity-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 0.55rem;
  transition: all 0.2s ease;
}

.entity-card:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.entity-card.expanded {
  border-color: #667eea;
}

/* Highlighted state - when navigated to from Decision Center */
.entity-card.highlighted {
  border-color: #f59e0b;
  border-width: 2px;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.2), 0 4px 12px -2px rgba(245, 158, 11, 0.3);
  animation: highlight-pulse 2s ease-in-out;
}

@keyframes highlight-pulse {
  0%, 100% {
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.2), 0 4px 12px -2px rgba(245, 158, 11, 0.3);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(245, 158, 11, 0.3), 0 4px 16px -2px rgba(245, 158, 11, 0.4);
  }
}

.entity-summary {
  padding: 0.85rem 1rem;
  cursor: pointer;
}

.entity-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.45rem;
}

.entity-info {
  flex: 1;
  min-width: 0;
}

.entity-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem 0.65rem;
  margin-bottom: 0.25rem;
}

.entity-info h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
}

.issue-headline {
  margin: 0 0 0.15rem;
  font-size: 0.9rem;
  color: #334155;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.issue-headline.issue-ok {
  color: #047857;
}

.issue-action {
  margin: 0;
  font-size: 0.84rem;
  color: #0f172a;
  line-height: 1.35;
}

.issue-action--watch {
  color: #b45309;
}

.issue-action-label {
  font-weight: 700;
  color: #64748b;
  margin-right: 0.2rem;
}

.status-badge {
  display: inline-block;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 650;
  letter-spacing: 0.02em;
  text-transform: capitalize;
}

.status-compliant {
  background: #d1fae5;
  color: #065f46;
}

.status-warning {
  background: #fed7aa;
  color: #92400e;
}

.status-partial {
  background: #ede9fe;
  color: #5b21b6;
}

.status-pending {
  background: #e0e7ff;
  color: #3730a3;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.record-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.65rem;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.record-link-btn:hover {
  background: #e0e7ff;
  border-color: #a5b4fc;
}

.continuity-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 500;
  border: 1px solid transparent;
  cursor: pointer;
}

.continuity-chip-neutral {
  background: #f1f5f9;
  color: #64748b;
  border-color: #e2e8f0;
}

.continuity-chip-ok {
  background: #ecfdf5;
  color: #047857;
  border-color: #a7f3d0;
}

.continuity-chip-warning {
  background: #fef2f2;
  color: #b91c1c;
  border-color: #fecaca;
}

.continuity-chip:hover {
  filter: brightness(0.97);
}

.continuity-mini-bar-track {
  height: 6px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.continuity-mini-bar-fill {
  height: 100%;
  border-radius: 4px;
  background: #22c55e;
}

.continuity-mini-bar-fill.bar-warning {
  background: #f59e0b;
}

.continuity-mini-bar-fill.bar-mixed {
  background: #6366f1;
}

.continuity-mini-meta {
  margin: 0 0 0.35rem;
  font-size: 0.875rem;
  color: #475569;
}

.continuity-mini-empty .doc-count {
  margin-bottom: 0.35rem;
}

.expand-btn {
  background: none;
  border: none;
  color: #667eea;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
}

.entity-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem 0.85rem;
  margin-bottom: 0;
  font-size: 0.8rem;
  color: #64748b;
}

.entity-stats span {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.entity-stats i {
  color: #94a3b8;
}

.entity-stats .stat-outreach--problem,
.entity-stats .stat-outreach--problem i {
  color: #c0392b;
}

.compliance-bar {
  margin-top: 1rem;
}

.progress-bar {
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.progress-success {
  background: linear-gradient(90deg, #10b981, #059669);
}

.progress-warning {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}

.progress-danger {
  background: linear-gradient(90deg, #ef4444, #dc2626);
}

.progress-text {
  font-size: 0.875rem;
  color: #64748b;
}

/* Expanded View */
.entity-details {
  border-top: 1px solid #e2e8f0;
  padding: 1.25rem;
}

.details-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e2e8f0;
}

.details-tabs button {
  background: none;
  border: none;
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
  color: #64748b;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.2s ease;
}

.details-tabs button:hover {
  color: #667eea;
}

.details-tabs button.active {
  color: #667eea;
  border-bottom-color: #667eea;
  font-weight: 500;
}

.tab-content {
  min-height: 200px;
}

.loading-state {
  text-align: center;
  padding: 2rem;
  color: #94a3b8;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #94a3b8;
  font-style: italic;
}

.status-summary h4,
.critical-gaps h4,
.main-entity h4,
.agents-section h4 {
  font-size: 1rem;
  margin-bottom: 1rem;
  color: #1a202c;
}

.critical-gaps {
  margin: 1.5rem 0;
  padding: 1rem;
  background: #fef2f2;
  border-left: 4px solid #ef4444;
  border-radius: 4px;
}

.gap-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #fecaca;
}

.gap-item:last-child {
  border-bottom: none;
}

.gap-severity {
  font-weight: 500;
  color: #dc2626;
}

.party-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 6px;
  margin-bottom: 0.75rem;
}

.party-info strong {
  display: block;
  margin-bottom: 0.25rem;
  color: #1a202c;
}

.party-info p {
  margin: 0.25rem 0;
  font-size: 0.875rem;
  color: #64748b;
}

.document-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.document-item:last-child {
  border-bottom: none;
}

.doc-parties {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0;
}

.party-badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 4px;
  font-size: 0.8125rem;
}

.doc-date {
  font-size: 0.875rem;
  color: #94a3b8;
}

.doc-status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.history-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.history-item:last-child {
  border-bottom: none;
}

.history-header {
  flex: 1;
}

.document-name {
  display: block;
  font-weight: 500;
  color: #1a202c;
  margin-bottom: 0.25rem;
}

.history-date {
  font-size: 0.875rem;
  color: #94a3b8;
}

.history-status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.entity-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: #667eea;
  color: white;
  border: none;
}

.btn-primary:hover {
  background: #5a67d8;
}

.btn-outline {
  background: white;
  color: #667eea;
  border: 1px solid #667eea;
}

.btn-outline:hover {
  background: #667eea;
  color: white;
}

.btn-success {
  background: #10b981;
  color: white;
  border: none;
}

.btn-success:hover {
  background: #059669;
}

.btn-danger-outline {
  color: #dc3545;
  border-color: #dc3545;
}

.btn-danger-outline:hover {
  background: #dc3545;
  color: white;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
}

/* Split View Layout */
.split-view {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1rem;
  padding: 0.85rem 1rem 1rem;
  align-items: start;
}

/* Left Panel (Overview) */
.overview-panel {
  border-right: 1px solid #e2e8f0;
  padding-right: 1rem;
}

.summary-section {
  margin-bottom: 0.85rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #f1f5f9;
}

.summary-section:last-of-type {
  border-bottom: none;
}

.summary-section h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-section h4 i {
  font-size: 1rem;
  color: #94a3b8;
}

.status-display {
  margin-bottom: 0.75rem;
}

.requirements-summary {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0.5rem 0;
}

.last-checked {
  font-size: 0.875rem;
  color: #94a3b8;
  margin: 0.5rem 0;
}

.doc-count {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0.5rem 0;
}

.expiring-warning {
  color: #f59e0b;
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0.5rem 0;
}

.btn-link {
  background: none;
  border: none;
  color: #667eea;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.btn-link:hover {
  color: #5a67d8;
  text-decoration: underline;
}

.panel-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1.5rem;
}

.panel-actions .btn {
  width: 100%;
  justify-content: center;
}

/* Right Panel (Details) */
.details-panel {
  overflow-y: auto;
  max-height: 520px;
  min-height: 0;
}

.detail-section {
  margin-bottom: 0.65rem;
  background: #f8fafc;
  border-radius: 8px;
  overflow: hidden;
}

.section-header {
  padding: 0.65rem 0.85rem;
  background: #f1f5f9;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s ease;
}

.section-header:hover {
  background: #e2e8f0;
}

.section-header h4 {
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.toggle-icon {
  color: #94a3b8;
  transition: transform 0.2s ease;
}

.section-content {
  padding: 1.25rem;
}

.loading-mini {
  text-align: center;
  padding: 1rem;
  color: #94a3b8;
  font-size: 0.875rem;
}

.empty-mini {
  text-align: center;
  padding: 1rem;
  color: #cbd5e0;
  font-style: italic;
  font-size: 0.875rem;
}

/* Location Cards */
.location-card {
  padding: 1rem;
  background: white;
  border-radius: 6px;
  margin-bottom: 0.75rem;
  border: 1px solid #e2e8f0;
}

.location-card:last-child {
  margin-bottom: 0;
}

.location-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.location-header strong {
  color: #1a202c;
  font-size: 0.9375rem;
}

.location-address {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0.5rem 0;
}

.location-stats {
  font-size: 0.875rem;
  color: #94a3b8;
  margin-top: 0.5rem;
}

.status-badge.mini {
  font-size: 0.75rem;
  padding: 0.125rem 0.5rem;
}

/* Agent Cards */
.agent-card {
  padding: 1rem;
  background: white;
  border-radius: 6px;
  margin-bottom: 0.75rem;
  border: 1px solid #e2e8f0;
}

.agent-card:last-child {
  margin-bottom: 0;
}

.agent-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.agent-header strong {
  color: #1a202c;
  font-size: 0.9375rem;
}

.role-badge {
  font-size: 0.75rem;
  padding: 0.125rem 0.5rem;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 4px;
}

.agent-contact p {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.agent-contact i {
  color: #94a3b8;
  width: 1rem;
}

.agent-stats {
  font-size: 0.875rem;
  color: #94a3b8;
  margin: 0.75rem 0;
}

/* Document rows — metadata first, not sparse cards */
.document-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.55rem 0.65rem;
  background: #fff;
  border-radius: 6px;
  margin-bottom: 0.4rem;
  border: 1px solid #e2e8f0;
}

.document-row:last-child {
  margin-bottom: 0;
}

.document-row-main {
  min-width: 0;
  flex: 1;
}

.doc-name {
  display: block;
  font-weight: 650;
  color: #0f172a;
  font-size: 0.88rem;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.doc-meta-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.25rem;
}

.doc-period {
  font-size: 0.78rem;
  font-weight: 650;
  color: #0f172a;
  margin-right: 0.15rem;
}

.doc-meta-muted {
  font-size: 0.78rem;
  color: #94a3b8;
}

.coverage-pill {
  display: inline-block;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  background: #f1f5f9;
  color: #334155;
  font-size: 0.7rem;
  font-weight: 650;
}

.doc-date {
  font-size: 0.75rem;
  color: #94a3b8;
  margin: 0.2rem 0 0;
}

.doc-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  flex-shrink: 0;
  margin-top: 0;
}

.doc-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.doc-header strong {
  color: #1a202c;
  font-size: 0.9375rem;
}

.doc-expiration {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0.25rem 0;
}

.doc-expiration.expiring {
  color: #f59e0b;
  font-weight: 500;
}

/* History Items */
.history-item {
  padding: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.history-item:last-child {
  border-bottom: none;
}

.history-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

.doc-name {
  font-weight: 500;
  color: #1a202c;
  font-size: 0.875rem;
}

.history-date {
  font-size: 0.875rem;
  color: #94a3b8;
}

.expired-date {
  font-size: 0.8125rem;
  color: #94a3b8;
  margin: 0;
}

/* Locations — edge status for covered / gap / unknown */
.locations-coverage-hint {
  margin: 0 0 0.55rem;
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.35;
}

.locations-badges-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding: 0;
}

.location-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.55rem 0.35rem 0.45rem;
  background: #f8fafc;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  border-left-width: 3px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.78rem;
  cursor: help;
}

.location-badge.loc-cov-covered {
  border-left-color: #059669;
}

.location-badge.loc-cov-gap {
  border-left-color: #d97706;
}

.location-badge.loc-cov-partial {
  border-left-color: #7c3aed;
}

.location-badge.loc-cov-unknown {
  border-left-color: #94a3b8;
}

.location-badge i {
  font-size: 0.7rem;
  color: #64748b;
}

.location-cov-label {
  margin-left: 0.15rem;
  font-size: 0.68rem;
  font-weight: 650;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

/* Analysis sessions — one status chip; lead with results */
.session-row {
  padding: 0.55rem 0.65rem;
  background: #f8fafc;
  border-radius: 6px;
  margin-bottom: 0.4rem;
  border: 1px solid #e2e8f0;
}

.session-row:last-child {
  margin-bottom: 0;
}

.session-row-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}

.session-name {
  font-weight: 650;
  color: #0f172a;
  font-size: 0.88rem;
  line-height: 1.3;
  min-width: 0;
}

.session-status {
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 650;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
}

.session-date {
  margin: 0.15rem 0 0.35rem;
  font-size: 0.75rem;
  color: #94a3b8;
}

.session-summary-text {
  margin: 0 0 0.35rem;
  font-size: 0.84rem;
  color: #334155;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.session-result-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem;
  margin-bottom: 0.4rem;
}

.session-counts {
  font-size: 0.78rem;
  color: #475569;
  margin-right: 0.2rem;
}

.session-item {
  padding: 1rem;
  background: #f8fafc;
  border-radius: 6px;
  margin-bottom: 0.75rem;
  border: 1px solid #e2e8f0;
}

.session-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.session-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9375rem;
}

.session-status {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8125rem;
  font-weight: 500;
}

.session-status.status-processing {
  background: #dbeafe;
  color: #1e40af;
}

.session-status.status-completed {
  background: #d1fae5;
  color: #065f46;
}

.session-status.status-failed {
  background: #fee2e2;
  color: #991b1b;
}

.session-date {
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0;
}

.session-summary {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #475569;
  font-style: italic;
}

.session-results {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e2e8f0;
}

.session-outcome {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.session-outcome-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #059669;
  font-weight: 600;
  font-size: 0.9rem;
}

.session-document-name {
  margin: 0;
  color: #334155;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.session-summary-text {
  margin: 0;
  color: #475569;
  font-size: 0.875rem;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.session-summary-muted {
  color: #64748b;
  font-style: italic;
}

.session-coverages {
  margin: 0;
  color: #475569;
  font-size: 0.875rem;
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
}

.session-processing,
.session-failed {
  margin-top: 0.5rem;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.session-processing {
  background: #dbeafe;
  color: #1e40af;
}

.session-failed {
  background: #fee2e2;
  color: #991b1b;
}

/* Agent Contacts Styling */
.agent-entity-contact {
  margin: 0.75rem 0;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 6px;
}

.agent-entity-contact .contact-item {
  margin: 0.25rem 0;
  font-size: 0.875rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.agent-entity-contact .contact-item i {
  color: #667eea;
  width: 16px;
}

.agent-contacts {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.contacts-subheader {
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.contact-person {
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  border: 1px solid #e2e8f0;
}

.contact-person:last-child {
  margin-bottom: 0;
}

.contact-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.contact-name strong {
  font-size: 0.9375rem;
  color: #1e293b;
}

.contact-title {
  font-size: 0.8125rem;
  color: #64748b;
  background: #e0e7ff;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
}

.contact-detail {
  margin: 0.25rem 0 0.25rem 1.75rem;
  font-size: 0.8125rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.contact-detail i {
  color: #667eea;
  width: 14px;
}

/* Responsive */
@media (max-width: 1024px) {
  .split-view {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .overview-panel {
    border-right: none;
    border-bottom: 2px solid #e2e8f0;
    padding-right: 0;
    padding-bottom: 1.5rem;
  }
  
  .panel-actions {
    flex-direction: row;
  }
  
  .panel-actions .btn {
    width: auto;
    flex: 1;
  }
}

@media (max-width: 768px) {
  .entity-header {
    flex-direction: column;
  }
  
  .entity-stats {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .entity-actions {
    flex-direction: column;
  }
  
  .details-tabs {
    overflow-x: auto;
  }
  
  .split-view {
    padding: 1rem;
  }
}
/* ── Contacts subsection ── */
.contacts-subsection,
.agents-subsection {
  margin-bottom: 16px;
}

.subsection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e9ecef;
}

.subsection-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}

/* Person-first insurance contact form */
.add-insurance-person-form {
  background: #f8f9ff;
  border: 1px solid #d0d7ff;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 12px;
}

.insurance-person-form-header {
  margin-bottom: 12px;
}

.insurance-person-form-header p.form-hint {
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: #6c757d;
}

.agency-optional-section {
  margin-top: 10px;
  padding: 10px;
  background: #fff;
  border: 1px dashed #adb5bd;
  border-radius: 6px;
}

.agency-optional-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  margin-bottom: 4px;
}

.optional-tag {
  background: #e9ecef;
  color: #6c757d;
  border-radius: 3px;
  padding: 1px 6px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
}

.agency-optional-hint {
  font-size: 0.78rem;
  color: #6c757d;
  margin: 0 0 8px;
  display: flex;
  align-items: flex-start;
  gap: 5px;
}

.agency-optional-hint i { margin-top: 2px; flex-shrink: 0; color: #0d6efd; }

.create-agency-hint { font-size: 0.82rem; margin-top: 4px; }

/* Standalone insurance contacts (no agency yet) */
.standalone-insurance-contacts {
  margin-bottom: 10px;
}

.standalone-contact-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px 12px;
  background: #fffbf0;
  border: 1px solid #ffd666;
  border-left: 3px solid #faad14;
  border-radius: 6px;
  margin-bottom: 6px;
}

.standalone-contact-info { flex: 1; }

.standalone-contact-name {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.standalone-contact-details {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 0.82rem;
  color: #555;
  margin-bottom: 4px;
}

.no-agency-badge {
  font-size: 0.75rem;
  color: #856404;
  display: flex;
  align-items: center;
  gap: 4px;
}

.ml-1 { margin-left: 4px; }
.ml-2 { margin-left: 8px; }

.subsection-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 6px;
}

.count-chip {
  background-color: #e9ecef;
  color: #495057;
  font-size: 0.7rem;
  padding: 1px 7px;
  border-radius: 10px;
  font-weight: 700;
}

.btn-xs {
  padding: 3px 10px;
  font-size: 0.75rem;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* Direct contact cards */
.direct-contacts-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
}

.direct-contact-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.direct-contact-info {
  flex: 1;
}

.direct-contact-name {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}

.direct-contact-name strong {
  font-size: 0.9rem;
  color: #2c3e50;
}

.contact-role-badge {
  background-color: #6610f2;
  color: white;
  font-size: 0.7rem;
  padding: 2px 7px;
  border-radius: 10px;
  font-weight: 600;
}

.primary-badge {
  background-color: #0d6efd;
  color: white;
  font-size: 0.7rem;
  padding: 2px 7px;
  border-radius: 10px;
  font-weight: 600;
}

.direct-contact-details {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 0.8rem;
  color: #6c757d;
}

.direct-contact-details i {
  margin-right: 3px;
}

.contact-title-text {
  font-style: italic;
}

.btn-remove-contact {
  background: none;
  border: none;
  color: #adb5bd;
  cursor: pointer;
  padding: 2px 5px;
  font-size: 0.8rem;
  transition: color 0.2s;
  flex-shrink: 0;
}

.btn-remove-contact:hover {
  color: #dc3545;
}

/* Inline add-contact form */
.add-contact-inline {
  background: #f8f9fa;
  border: 1px dashed #ced4da;
  border-radius: 8px;
  padding: 14px;
  margin-top: 8px;
}

.contact-form-row {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.contact-form-field {
  flex: 1;
  min-width: 0;
}

.contact-form-field label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 4px;
}

.contact-form-field .req {
  color: #dc3545;
}

.form-ctrl {
  display: block;
  width: 100%;
  padding: 7px 10px;
  font-size: 0.875rem;
  color: #495057;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 5px;
  box-sizing: border-box;
}

.form-ctrl:focus {
  border-color: #0d6efd;
  outline: none;
  box-shadow: 0 0 0 0.15rem rgba(13,110,253,0.2);
}

.contact-form-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.contact-form-error {
  background: #f8d7da;
  color: #842029;
  border: 1px solid #f5c2c7;
  border-radius: 4px;
  padding: 7px 10px;
  font-size: 0.85rem;
  margin-bottom: 10px;
}

.email-warning {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  border-radius: 4px;
  padding: 5px 8px;
  font-size: 0.8rem;
  margin-top: 4px;
}

.email-warning i {
  margin-top: 2px;
  flex-shrink: 0;
}

.email-warning-error {
  background: #f8d7da;
  color: #842029;
  border: 1px solid #f5c2c7;
}

.email-warning-caution {
  background: #fff3cd;
  color: #664d03;
  border: 1px solid #ffecb5;
}

@media (max-width: 576px) {
  .contact-form-row {
    flex-direction: column;
  }
}

/* ── Coverage pills ── */
.coverage-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 6px 0 10px;
}

.coverage-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.4px;
}

.coverage-pill-all {
  background: #f1f5f9;
  color: #64748b;
  font-weight: 500;
}

/* ── Agent card tweaks ── */
.agent-header-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.auto-badge {
  font-size: 0.7rem;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 6px;
  cursor: default;
}

.agent-card-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.agent-notes {
  font-size: 0.8rem;
  color: #64748b;
  font-style: italic;
  margin: 4px 0;
}

.agent-notes i {
  margin-right: 4px;
}

/* ── Add Agent form ── */
.add-agent-inline {
  background: #f8f9fa;
  border: 1px dashed #ced4da;
  border-radius: 8px;
  padding: 14px;
  margin-top: 10px;
}

.agent-form-explainer {
  font-size: 0.82rem;
  color: #495057;
  background: #e8f4fd;
  border-left: 3px solid #3b82f6;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 14px;
  line-height: 1.5;
}

.agent-form-explainer i {
  color: #3b82f6;
  margin-right: 5px;
}

.agent-form-step {
  margin-bottom: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e9ecef;
}

.agent-form-step:last-of-type {
  border-bottom: none;
}

.agent-form-step-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: #343a40;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: #667eea;
  color: white;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: 800;
  flex-shrink: 0;
}

.step-hint {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 400;
}

.agent-contact-toggle {
  margin-top: 4px;
}

.btn-add-person {
  background: none;
  border: 1px dashed #667eea;
  color: #667eea;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 0.82rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
}

.btn-add-person:hover {
  background: #e0e7ff;
}

.agent-contact-fields {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 12px;
}

.btn-text-sm {
  background: none;
  border: none;
  color: #dc3545;
  font-size: 0.78rem;
  cursor: pointer;
  padding: 4px 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
}

.btn-text-sm:hover {
  text-decoration: underline;
}

/* Agent search */
.agent-search-wrap {
  position: relative;
}

.search-spinner {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #667eea;
  font-size: 0.85rem;
}

.agent-search-results {
  border: 1px solid #ced4da;
  border-radius: 6px;
  background: #fff;
  margin-top: 4px;
  max-height: 180px;
  overflow-y: auto;
}

.agent-search-result-item {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;
}

.agent-search-result-item:last-child {
  border-bottom: none;
}

.agent-search-result-item:hover {
  background: #e0e7ff;
}

.result-type {
  font-size: 0.7rem;
  background: #e0e7ff;
  color: #3730a3;
  padding: 1px 6px;
  border-radius: 8px;
  font-weight: 600;
}

.result-email {
  color: #94a3b8;
  margin-left: auto;
}

.agent-create-new {
  margin-top: 6px;
  padding: 8px 12px;
  background: #d1fae5;
  border-radius: 6px;
  font-size: 0.85rem;
  color: #065f46;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
}

.agent-create-new:hover {
  background: #a7f3d0;
}

.selected-agent-chip {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.chip-type {
  font-size: 0.7rem;
  background: #c7d2fe;
  padding: 1px 6px;
  border-radius: 8px;
}

.chip-clear {
  background: none;
  border: none;
  color: #3730a3;
  cursor: pointer;
  padding: 0 2px;
  font-size: 0.8rem;
  line-height: 1;
}

.chip-clear:hover {
  color: #dc2626;
}

/* Coverage checkboxes grid */
.coverage-checkboxes {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 4px 12px;
  margin-top: 6px;
}

.coverage-checkbox-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: #495057;
  cursor: pointer;
  padding: 3px 0;
}

.coverage-checkbox-item input {
  cursor: pointer;
  accent-color: #667eea;
}

.btn-secondary {
  background: #6c757d;
  color: white;
  border: none;
}

.btn-secondary:hover {
  background: #5a6268;
}

/* ── Edit mode ── */
.agent-edit-header {
  font-size: 0.85rem;
  color: #667eea;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e7ff;
}

.btn-text-link {
  background: none;
  border: none;
  color: #667eea;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;
  margin-left: 6px;
  text-decoration: underline;
  font-weight: 400;
}

.btn-text-link:hover {
  color: #5a67d8;
}

/* ── Contacts at agency (view + edit) ── */
.agency-contacts-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 6px 0 8px;
  padding: 8px 10px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.agency-contact-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 0.8rem;
  color: #475569;
}

.agency-contact-row i {
  color: #94a3b8;
  width: 14px;
}

.agency-contact-name {
  font-weight: 600;
  color: #1e293b;
}

.contact-role-badge.sm {
  font-size: 0.65rem;
  padding: 1px 5px;
  border-radius: 8px;
  background: #e0e7ff;
  color: #3730a3;
  font-weight: 700;
}

.agency-contact-detail {
  color: #64748b;
  font-size: 0.75rem;
}

.agency-edit-contacts {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
}

.agency-edit-contact-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 6px 10px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.8rem;
}

.agency-edit-contact-row .btn-remove-contact {
  margin-left: auto;
}
/* ── Edit Entity Modal ──────────────────────────────────────────────────── */
.edit-entity-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.edit-entity-dialog {
  background: #fff;
  border-radius: 10px;
  width: 560px;
  max-width: 95vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
}

.edit-entity-dialog .modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px 14px;
  border-bottom: 1px solid #e9ecef;
}

.edit-entity-dialog .modal-header h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
}

.btn-close-modal {
  background: none;
  border: none;
  font-size: 1.4rem;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
  padding: 0 4px;
}

.btn-close-modal:hover { color: #343a40; }

.edit-entity-dialog .modal-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}

.edit-entity-dialog .modal-footer {
  padding: 14px 24px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.edit-entity-dialog .form-label {
  font-weight: 500;
  font-size: 0.875rem;
  margin-bottom: 4px;
  color: #495057;
  display: block;
}

.edit-entity-dialog .form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.edit-loading {
  text-align: center;
  padding: 40px 0;
  color: #6c757d;
  font-size: 0.95rem;
}

.alert-danger {
  background: #fff5f5;
  border: 1px solid #f5c2c7;
  color: #842029;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 0.875rem;
}
</style>

